<template>
  <q-page
    id="collect-data"
    class="q-py-sm"
    :class="{
      'mobile-video-chat-opened': hideSessionComponent,
      'q-px-sm': $q.platform.is.mobile,
      'q-px-lg': !$q.platform.is.mobile,
    }"
  >
    <div
      v-if="preventSessionStart"
      class="full-width row items-center justify-center q-my-xl"
    >
      {{ sessionInactiveMessage }}
    </div>
    <template
      v-else-if="hasActiveSession"
    >
      <q-header
        ref="header"
        elevated
        class="row items-center q-pa-xs text-black"
        :class="{
          'bg-grey-4': !isCurrentSessionOwner || isSessionTimerPaused,
          'collect-data-safe-padding-top': isNativeIos
        }"
      >
        <q-tabs
          v-if="isGroupSession"
          :model-value="activeSession.id"
          dense
          active-bg-color="primary"
          active-color="white"
          indicator-color="primary"
          no-caps
          class="col-12 bg-white"
          @update:model-value="onSwitchGroupSession"
        >
          <q-tab
            v-for="session in visibleGroupSessions"
            :key="session.id"
            :name="session.id"
            :disable="!networkOnline"
            :data-e2e="`tab-participant-${participantTabId(session.client)}`"
          >
            <div class="row items-center no-wrap">
              <div>
                <q-avatar
                  size="24px"
                  class="q-mr-sm shadow-2"
                >
                  <q-img :src="getClientPhoto(session.client)" />
                </q-avatar>
              </div>
              <div>
                {{
                  getClientProperty(session.client, 'alias')
                    || getClientProperty(session.client, 'clientName') }}
              </div>
            </div>
          </q-tab>
          <template v-if="userHasPrivilege('sessions/startSessions')">
            <q-btn
              v-if="unstartedGroupSessionAppointments.length === 0"
              icon="add"
              color="primary"
              flat
              :loading="isAddingGroupParticipant"
              :disable="!networkOnline"
              @click="onAddGroupParticipant()"
            />
            <q-btn-dropdown
              v-else
              color="primary"
              :loading="isAddingGroupParticipant"
              :disable="isAddingGroupParticipant || !networkOnline"
              flat
              icon="add"
            >
              <q-list>
                <q-item
                  v-for="appointment in unstartedGroupSessionAppointments"
                  :key="appointment.id"
                  v-close-popup
                  clickable
                  @click="onAddGroupParticipantFromAppointment(appointment)"
                >
                  <q-item-section avatar>
                    <q-avatar
                      size="24px"
                      class="q-mr-sm shadow-2"
                    >
                      <q-img :src="getClientPhoto(appointment.client)" />
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{
                        getClientProperty(appointment.client, 'alias')
                          || getClientProperty(appointment.client, 'clientName') }}
                    </q-item-label>
                    <q-item-label caption>
                      {{ appointment.userName }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-separator />
                <q-item
                  v-close-popup
                  clickable
                  @click="onAddGroupParticipant()"
                >
                  <q-item-section avatar>
                    <q-avatar
                      size="24px"
                      class="q-mr-sm shadow-2"
                    >
                      <q-icon
                        name="add"
                        color="primary"
                      />
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('run.collectData.labelAddParticipant') }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </template>
        </q-tabs>
        <div
          v-if="!isLoading"
          class="col-12 row q-pa-xs"
        >
          <div class="col row q-col-gutter-sm no-wrap pinned-targets">
            <frequency-timer
              v-if="activeSession"
              ref="sessionTimer"
              v-model="activeSession.duration"
              :paused="isSessionTimerPaused"
              :duration-offset="sessionDurationOffset"
              dense
              tiny
              include-hours
              :disable="!isCurrentSessionOwner"
              :pause-message="$t('run.collectData.sessionPaused')"
              @start="onShowPauseDialog"
              @pause="onPauseSessionTimer"
            />
            <!-- Pinned programs -->
            <template
              v-for="(sessionProgram, sessionProgramIndex) in pinnedSessionPrograms"
              :key="sessionProgram.id"
            >
              <div class="text-center row column q-col-gutter-xs">
                <div>
                  <q-btn
                    :icon="getPinnedProgramIcon(sessionProgram.program)"
                    size="sm"
                    color="white"
                    flat
                    round
                    dense
                    @click="onShowProgramInfo(sessionProgram.program)"
                  >
                    <q-tooltip>{{ sessionProgram.program.programName }}</q-tooltip>
                  </q-btn>
                </div>
                <div
                  v-if="canUndo(sessionProgram)"
                >
                  <q-btn
                    icon="undo"
                    size="sm"
                    color="white"
                    flat
                    round
                    dense
                    :disable="isSessionTimerPaused"
                    @click="onUndoData(sessionProgram)"
                  >
                    <q-tooltip>
                      {{ $t('common.buttonUndo') }}
                    </q-tooltip>
                  </q-btn>
                </div>
              </div>
              <program-data-collection
                :ref="'sessionProgram_' + sessionProgram.id"
                :session-program="sessionProgram"
                :sorted-targets="getSortedTargets(sessionProgram)"
                :data="getSessionProgramData(sessionProgram)"
                dense
                tiny
                visible
                :disable="!isCurrentSessionOwner || isSessionTimerPaused"
                :active-prompt-levels="activePromptLevels"
                :workflow-statuses="workflowStatuses"
                :session-type="activeSession.sessionType"
                :session-paused="isSessionTimerPaused"
                :session-duration-offset="sessionDurationOffset"
                :ios-audio="getAudioBySession(sessionProgram.id)"
                :cached-current-program-trial="currentTrial[sessionProgram.id] || 0"
                @record="onRecordData"
                @record-multiple-data="onRecordMultipleData"
                @record-program-data="onRecordProgramData"
                @refresh-targets="onRefreshTargets"
                @change-target-status="onChangeTargetStatus"
                @automation-action-completed="onAutomationActionCompleted"
              />
              <div
                v-if="canEditProgramNotes(sessionProgram)"
              >
                <q-btn
                  icon="notes"
                  size="sm"
                  color="white"
                  flat
                  round
                  dense
                  @click="onEditProgramNotes(sessionProgram)"
                >
                  <q-tooltip>
                    {{ $t('run.collectData.inputProgramNotesLabel') }}
                  </q-tooltip>
                </q-btn>
              </div>
              <div
                v-if="sessionProgramIndex != Object.keys(pinnedSessionPrograms).length - 1"
                class="row"
              >
                <q-separator
                  vertical
                  inset
                  class="q-ml-sm"
                />
              </div>
            </template>
          </div>
          <q-space />
          <div class="col-auto row self-center items-center">
            <offline-mode-indicator
              v-if="enableOfflineModeDialog || !networkOnline"
              v-model:warning-visible="warningVisible"
              v-model:success-visible="successVisible"
              :enable-offline-mode-dialog="enableOfflineModeDialog"
              :no-label="$q.screen.lt.sm"
            />
            <upload-progress />
            <behaviour-program-data
              color="white"
              :program="currentBehaviorProgram"
              :session="activeSession"
            />
          </div>
        </div>
      </q-header>
      <offline-mode-banners
        v-if="enableOfflineModeDialog"
        :warning-visible="warningVisible"
        :success-visible="successVisible"
        banner-class="q-mb-md"
        @dismiss-warning="dismissWarning"
        @dismiss-success="dismissSuccess"
        @switch-offline="switchOfflineMode"
        @switch-online="switchOfflineMode"
      />
      <div
        v-if="!isLoading"
        class="row wrap"
        :class="{ 'collect-data-container': hideSessionComponent }"
      >
        <div
          v-if="!hideSessionComponent"
          class="col-lg-7 col-md-7 col-xs-12"
          :class="{ 'col-lg-12 col-md-12':
            !isVideoCallCreated || isVideoMinimized || ((isNativeAndroid || isNativeIos) && isLandscape)
          }"
        >
          <!-- Unpinned programs -->
          <div
            v-for="sessionProgram in availableSessionPrograms"
            :key="sessionProgram.id"
          >
            <div
              v-show="showSessionProgram(sessionProgram)"
              class="q-mb-md"
            >
              <div
                v-if="!hideTitle(sessionProgram)"
                class="row items-center q-pb-md"
              >
                <div
                  class="q-my-none ellipsis col text-overline text-dark text-uppercase"
                  data-e2e="text-program-name"
                >
                  <behavior-tracking-icon
                    v-if="isBehaviorTrackingProgram(sessionProgram.program)"
                    size="sm"
                  />
                  {{ sessionProgram.program.programName }}
                </div>
                <div class="col-auto row q-col-gutter-sm">
                  <div>
                    <q-btn
                      v-if="canUndo(sessionProgram)"
                      v-ripple
                      icon="undo"
                      color="dark"
                      flat
                      dense
                      :round="hideButtonLabels"
                      :disable="isSessionTimerPaused"
                      @click="onUndoData(sessionProgram)"
                    >
                      <q-tooltip>
                        {{ $t('common.buttonUndo') }}
                      </q-tooltip>
                    </q-btn>
                  </div>
                  <div>
                    <q-btn
                      v-if="hasInstructions(sessionProgram)"
                      v-ripple
                      icon="info"
                      color="dark"
                      flat
                      dense
                      :round="hideButtonLabels"
                      :label="
                        hideButtonLabels
                          ? '' : $t('run.collectData.buttonInstructions')
                      "
                      @click="onShowProgramInfo(sessionProgram.program)"
                    >
                      <q-tooltip
                        v-if="hideButtonLabels"
                      >
                        {{ $t('run.collectData.buttonInstructions') }}
                      </q-tooltip>
                    </q-btn>
                  </div>
                  <div>
                    <q-btn
                      v-if="hasHistory(sessionProgram) && !offlineMode"
                      icon="history"
                      flat
                      color="dark"
                      dense
                      :round="hideButtonLabels"
                      @click="onShowProgramInfo(sessionProgram.program, 'history')"
                    >
                      <q-tooltip>
                        {{ $t('run.collectData.buttonHistory') }}
                      </q-tooltip>
                    </q-btn>
                  </div>
                  <template
                    v-if="expandedView"
                  >
                    <div
                      v-if="canEditProgramNotes(sessionProgram)"
                    >
                      <q-btn
                        icon="notes"
                        color="dark"
                        flat
                        round
                        dense
                        @click="onEditProgramNotes(sessionProgram)"
                      >
                        <q-tooltip>
                          {{ $t('run.collectData.inputProgramNotesLabel') }}
                        </q-tooltip>
                      </q-btn>
                    </div>
                    <div
                      v-if="!isFiltering"
                    >
                      <q-btn
                        v-ripple
                        color="dark"
                        round
                        dense
                        flat
                        icon="fullscreen_exit"
                        size="md"
                        @click="onContractView(sessionProgram)"
                      >
                        <q-tooltip>
                          {{ $t('run.collectData.tooltipExitExpanded') }}
                        </q-tooltip>
                      </q-btn>
                    </div>
                  </template>
                </div>
              </div>
              <!-- program-data-collection -->
              <program-data-collection
                :ref="'sessionProgram_' + sessionProgram.id"
                :key="'program_' + sessionProgram.id"
                :session-program="sessionProgram"
                :sorted-targets="getSortedTargets(sessionProgram)"
                :data="getSessionProgramData(sessionProgram)"
                :dense="expandedView"
                :disable="!isCurrentSessionOwner || isSessionTimerPaused"
                :active-prompt-levels="activePromptLevels"
                :workflow-statuses="workflowStatuses"
                :session-type="activeSession.sessionType"
                :session-paused="isSessionTimerPaused"
                :session-duration-offset="sessionDurationOffset"
                :visible="showSessionProgram(sessionProgram)"
                :ios-audio="getAudioBySession(sessionProgram.id)"
                :cached-current-program-trial="currentTrial[sessionProgram.id] || 0"
                class="self-center"
                @record="onRecordData"
                @record-multiple-data="onRecordMultipleData"
                @record-program-data="onRecordProgramData"
                @refresh-targets="onRefreshTargets"
                @change-target-status="onChangeTargetStatus"
                @automation-action-completed="onAutomationActionCompleted"
              />
            </div>
            <q-separator
              v-if="expandedView && showSessionProgram(sessionProgram)"
              class="q-mb-lg"
            />
          </div>
          <div
            v-if="showNotesSection && !expandedView"
            class="full-width q-my-lg"
          >
            <q-input
              :key="currentSessionProgram?.id"
              v-model="programNotes"
              type="textarea"
              autogrow
              outlined
              hide-bottom-space
              :label="$t('run.collectData.inputProgramNotesLabel')"
              :disable="!isCurrentSessionOwner"
              class="col"
              debounce="2000"
              data-e2e="input-notes"
              @change="onInputProgramNotes"
            />
          </div>
          <div
            v-if="showMediaButtons"
            class="row q-col-gutter-md justify-center q-pb-md"
          >
            <div
              v-if="canShowSessionNotes"
            >
              <q-btn
                v-ripple
                round
                icon="speaker_notes"
                :color="sessionNotesButtonColor"
                :disable="!isCurrentSessionOwner"
                data-e2e="button-session-notes"
                @click="onSwitchSessionNotes"
              >
                <q-tooltip>
                  {{
                    isBackupDifferent && !offlineMode && networkOnline && !isSavingSessionNotes
                      ? $t('run.collectData.btnSessionNotesBackupHasDifference')
                      : $t('run.collectData.btnSessionNotesTooltip')
                  }}
                </q-tooltip>
              </q-btn>
            </div>
            <file-attachment
              v-if="activeSession"
              ref="attachment"
              :session="activeSession"
              round
              icon="photo_camera"
              size="md"
              color="primary"
              accept="image/*"
              class="col-auto"
              :file-path="'/sessions/' + activeSession.id + '/'"
              :disable="!isCurrentSessionOwner || offlineMode"
            >
              <q-badge
                v-if="photosUploaded"
                color="positive"
                floating
              >
                {{ photosUploaded }}
              </q-badge>
              <q-tooltip>
                {{ $t('run.collectData.btnAddPhotoTooltip') }}
              </q-tooltip>
            </file-attachment>
            <file-attachment
              v-if="activeSession"
              ref="attachment"
              :session="activeSession"
              round
              icon="videocam"
              size="md"
              color="primary"
              accept="video/mp4,video/*"
              :file-path="'/sessions/' + activeSession.id + '/'"
              :disable="!isCurrentSessionOwner || offlineMode"
              class="col-auto"
            >
              <q-badge
                v-if="videosUploaded"
                color="positive"
                floating
              >
                {{ videosUploaded }}
              </q-badge>
              <q-tooltip>
                {{ $t('run.collectData.btnAddVideoTooltip') }}
              </q-tooltip>
            </file-attachment>
            <template
              v-if="!isVideoCallCreated && !isInvitedUser"
            >
              <div
                v-if="canUserInviteToCall"
              >
                <q-btn
                  round
                  color="green"
                  icon="voice_chat"
                  :loading="isShowVideoCallBtnLoader"
                  :disable="isShowVideoCallBtnLoader || offlineMode || isGroupSession"
                  class="col-auto"
                  data-e2e="button-invite-video"
                  @click="openInvitationModal"
                >
                  <q-tooltip>
                    {{ $t('run.collectData.buttonCreateVideoCall') }}
                  </q-tooltip>
                </q-btn>
              </div>
              <div
                v-else-if="canUserJoinVideoCall"
              >
                <q-btn
                  round
                  color="green"
                  icon="voice_chat"
                  :loading="isShowVideoCallBtnLoader"
                  :disable="isShowVideoCallBtnLoader || offlineMode || isGroupSession"
                  class="col-auto"
                  data-e2e="button-invite-video"
                  @click="establishVideoCall"
                >
                  <q-tooltip>
                    {{ $t('run.collectData.buttonConnectToVideoCall') }}
                  </q-tooltip>
                </q-btn>
              </div>
            </template>
            <div
              v-else-if="!isVideoCallCreated && isInvitedUser"
            >
              <q-btn
                round
                color="green"
                icon="voice_chat"
                :loading="isShowVideoCallBtnLoader"
                :disable="isShowVideoCallBtnLoader || offlineMode || isGroupSession"
                class="col-auto"
                data-e2e="button-invite-video"
                @click="connectToVideoCall"
              >
                <q-tooltip>
                  {{ $t('run.collectData.buttonConnectToVideoCall') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div
              v-else-if="isShowReturnToVideoCallBtn && isVideoCallCreated && (isNativeAndroid || isNativeIos)"
            >
              <q-btn
                round
                color="green"
                icon="chevron_right"
                class="col-auto"
                @click="moveToVideoCallScreen"
              >
                <q-tooltip>
                  {{ $t('run.collectData.buttonReturnToVideoCall') }}
                </q-tooltip>
              </q-btn>
            </div>
            <div
              v-else-if="isVideoCallCreated && isVideoMinimized"
            >
              <q-btn
                round
                color="green"
                icon="open_in_full"
                class="col-auto"
                @click="onRestoreVideoFromMinimized"
              >
                <q-tooltip>
                  {{ $t('run.collectData.buttonRestoreVideo') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
          <template
            v-if="showSessionNotes"
          >
            <q-separator
              ref="sessionNotes"
              class="q-mb-lg"
            />
            <div
              class="q-my-none ellipsis col text-overline text-dark text-uppercase"
            >
              {{ $t('run.collectData.titleSessionNotes') }}
            </div>
            <session-activity-notes-editor
              v-if="hasCurrentSessionActivity"
              :label="$t('run.collectData.buttonEditSessionNotes')"
              :session-activity="currentSessionActivity"
              :session="activeSession"
              :all-programs="allPrograms"
              :client="sessionClient"
              :is-backup-different="isBackupDifferent && !offlineMode && networkOnline && !isSavingSessionNotes"
              :linked-appointment="linkedAppointment"
              auto-save
              @has-note-template="noteTemplateSelected"
              @show-backup-notes="onShowBackupNotes"

              @save="onSaveSessionActivityNotes(currentSessionActivity, $event)"
            />
          </template>
          <q-slide-transition>
            <session-progress
              :session-programs="availableSessionPrograms"
              :current-session-program="currentSessionProgram"
              :session="activeSession"
              :is-current-session-owner="isCurrentSessionOwner"
              :hide-session-programs="expandedView || isStaffTrainingSession"
              :hide-program-navigation="expandedView || !hasMultipleSessionPrograms"
              :hide-full-screen-switch="isStaffTrainingSession || isFiltering"
              :hide-filter="isStaffTrainingSession"
              :filtered-tags="filteredTags"
              :is-active-session-progress="isActiveSessionProgress"
              :can-participate="canParticipate"
              :can-stop-participation="isParticipating"
              @goto="onGotoSessionProgram"
              @change-target-status-selected="onChangeTargetStatusSelected"
              @expand="onExpandView"
              @contract="onContractView"
              @filter="onFilterPrograms"
              @finish-session="onFinishSession"
              @exit-session="onExitSession"
              @participate="onParticipate"
              @stop-participation="onStopParticipation"
              @request-session-control="onRequestSessionControl"
              @refresh-programs="onRefreshProgramsAndTargets"
            />
          </q-slide-transition>
        </div>
        <mobile-video-container
          v-if="isVideoCallCreated && (isNativeAndroid || isNativeIos)"
          ref="mobileVideoContainer"
          :active-session="activeSession"
          :current-client="sessionClient"
          :class="
            isLandscape
              ? 'col-lg-12 col-md-12 col-xs-12'
              : 'col-lg-4 col-md-4 col-xs-12 offset-md-1 offset-xs-0'
          "
          @finish-session="closeVideoChat"
          @move-to-session-screen="moveToSessionScreen"
        />
        <video-container
          v-if="isVideoCallCreated && !isNativeIos && !isNativeAndroid"
          v-show="!isVideoMinimized"
          ref="videoContainer"
          :active-session="activeSession"
          :header-height-px="headerHeightPx"
          :current-client="sessionClient"
          @finish-session="closeVideoChat"
          @minimize="onMinimizeVideo"
        />
      </div>
    </template>
  </q-page>
</template>
<script>
import OfflineModeBanners from 'components/Offline/OfflineModeBanners';
import OfflineModeIndicator from 'components/Offline/OfflineModeIndicator';
import BehaviorTrackingIcon from 'components/Programs/BehaviorTrackingIcon';
import WaitForSessionControl from 'components/Programs/DataCollection/WaitForSessionControl';
import EditNotes from 'components/Programs/Modals/EditNotes';
import IntervalSoundConfirm from 'components/Programs/Modals/IntervalSoundConfirm.vue';
import SelectClientUser from 'components/UI/modals/SelectClientUser';
import { arrayRemove, arrayUnion, Timestamp } from 'firebase/firestore';
import {
  assign,
  chain,
  find,
  get,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isString,
  last,
  map,
  mapValues,
  maxBy,
  omit,
  pickBy,
  reject,
  size,
  some,
  sortBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { scroll, uid } from 'quasar';
import { useClients } from 'src/composables/clients';
import { useOfflineMode } from 'src/composables/useOfflineMode';
import { BEHAVIOR_DATA_CONTEXTS } from 'src/constants/analytics';
import { FADING_STRATEGIES } from 'src/constants/prompt-level-template';
import { IS_INVITATION_ACCEPTED } from 'src/constants/videoSession';
import AppointmentsDB from 'src/fb/appointments-db';
import DataDB from 'src/fb/data-db';
import RealTimeNotificationsDB from 'src/fb/realTimeNotifications-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import { BEHAVIOR_TRACKING_ICON } from 'src/constants/programs';
import SessionsDB from 'src/fb/sessions-db';
import { frozenSessionOffsetFields } from 'src/utils/sessionPauseTimers';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { behaviorTrackingAnalytics } from 'src/services/analytics';
import Auth from 'src/mixins/Auth';
import CollectDataMixin from 'src/mixins/CollectDataMixin';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { programs } from 'src/store/programs';
import { programsTargets } from 'src/store/programsTargets';
import { sessions } from 'src/store/sessions';
import { workflows } from 'src/store/workflows';
import createInactivityTracking from 'src/utils/activityTrackingUtils';
import AppUtils from 'src/utils/AppUtils';
import { BACKUP_DATA, BACKUP_NOTES, clearBackupsExceptCurrentSession } from 'src/utils/backupUtils';
import { isBehaviorOccurrence, toBehaviorOccurrenceData } from 'src/utils/behaviorOccurrences';
import { logEvent } from 'src/utils/LogEvent';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';
import { isQuotaExceededError, notifyStorageQuotaExceeded } from 'src/utils/StorageQuotaUtils';

export default {
  name: 'CollectData',

  components: {
    BehaviorTrackingIcon,
    OfflineModeBanners,
    OfflineModeIndicator,
  },

  mixins: [
    CollectDataMixin,
    Interactions,
    SessionActivitiesMixin,
    Auth,
  ],

  provide() {
    return {
      recordBehaviorOccurrences: this.onRecordBehaviorOccurrences,
    };
  },

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      // Reload contents of edited program
      vm.setLoading(true);

      try {
        vm.loadSettings();
        await vm.initSession();
        if (
          to.query.finish === 'true'
          && vm.isCurrentSessionOwner
          && !['done', 'offline-done', 'processing'].includes(get(vm.activeSession, 'status'))
        ) {
          vm.onFinishSession();
        }

        if (['done', 'offline-done', 'processing'].includes(get(vm.activeSession, 'status'))) {
          return vm.handleFinishedSession();
        }

        const isInvitationAccepted = sessionStorage.getItem(IS_INVITATION_ACCEPTED);
        const activeVideoSession = await vm.getActiveVideoSession();
        if (!!activeVideoSession && (isInvitationAccepted || activeVideoSession.runBy === vm.userDetails.userId)) {
          vm.$q
            .dialog({
              title: vm.$t('sessions.modals.connectToCall.reconnectTitle'),
              message: vm.$t('sessions.modals.connectToCall.reconnectMessage'),
              cancel: false,
              persistent: true,
            })
            .onOk(() => vm.createVideoCall(null, false));
        }
      } catch (error) {
        console.error(error.message);
        logEvent(error);
        vm.notifyNegative(error.message);
      } finally {
        vm.setLoading(false);

        vm.$nextTick(async () => {
          if (vm.$route.query.join_call_with) {
            await vm.createVideoCall(vm.$route.query.join_call_with, true);
          } else {
            await vm.handleVideoCallInvitation(!!vm.$route.query.join_call);
          }

          vm.$router.replace({
            query: {
              ...omit(vm.$route.query, ['join_call_with', 'join_call']),
            },
          });
        });

        vm.setupInactivityTracking();
      }
    });
  }, // beforeRouteEnter()

  async beforeRouteLeave(to, from, next) {
    this.removeInactivityTracking();

    this.$q.loading.hide();

    if (this.unsavedChanges && !this.isAuthSessionExpired) {
      if (this.confirmStayOnPage()) {
        next(false);
        return;
      }
    }

    this.unbindData();
    this.unbindGroupPrograms();
    this.unbindUnstartedGroupAppointments();
    await this.unbindBackupsData();
    await this.unbindBackupNotes();

    if (this.isGroupSession) {
      await this.leaveGroupSession();
    }

    if (!to.path.includes('collect') && !to.path.includes('supervise')) {
      try {
        await this.finishVideoSession();
      } catch (e) {
        console.error(e);
        logEvent(e);
        this.notifyNegative(this.$t('session.videoSession.errorFinishingVideoSession', { error: e?.message || e }));
      }
    }
    next();
  }, // beforeRouteLeave()

  setup() {
    const { getClientProfileImagePath } = useClients();

    const {
      switchOfflineMode, enableOfflineModeDialog, warningVisible, successVisible, dismissWarning, dismissSuccess,
    } = useOfflineMode();

    return {
      getClientProfileImagePath,
      switchOfflineMode,
      enableOfflineModeDialog,
      warningVisible,
      successVisible,
      dismissWarning,
      dismissSuccess,
    };
  },

  data() {
    return {
      unstartedGroupSessionAppointments: [],
      sessionPauseGeneration: 0,

      editProgramNotes: '',
      unsavedNotesChanges: false,
      showSessionNotes: false,
      isBackupDifferent: false,
      showBackupData: false,
      hasActiveNoteTemplate: true,

      showAudioDialog: false,
      iosIntervalAudios: null,
      isSavingSessionNotes: false,
      inactivityTracker: null,
      isLandscape: window.matchMedia('(orientation: landscape)').matches,
      headerHeightPx: null,
    };
  },

  computed: {
    ...mapState(auth, ['currentClient', 'clientAppointmentsInProgress',
      'hasTherapistCredentials', 'visibilityOptions', 'hasSupervisorCredentials', 'isAuthSessionExpired']),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(sessions, ['activeSessionGroup', 'groupSessions', 'isTrainingVideoPlaying']),
    ...mapState(programs, ['programs', 'cachedGroupPrograms', 'allPrograms']),
    ...mapState(workflows, ['workflowStatuses']),

    hasActiveSession() {
      return this.activeSession !== null;
    },

    isCurrentSessionOwner() {
      return this.userDetails.userId === get(this.activeSession, 'runBy');
    }, // isCurrentSessionOwner()

    isCurrentSessionSupervisor() {
      return this.userDetails.userId === this.activeSession.supervisedBy;
    }, // isCurrentSessionSupervisor()

    isGroupSession() {
      return size(this.visibleGroupSessions) > 0;
    },

    visibleGroupSessions() {
      return sortBy(
        this.groupSessions,
        (groupParticipant) => (
          chain(this.activeSessionGroup)
            .get('groupParticipants')
            .find({ session: groupParticipant.id })
            .get('order')
            .value()
        ),
      );
    }, // visibleGroupSessions()

    groupSessionClientIds() {
      return chain(this.groupSessions)
        .map('client')
        .uniq()
        .value();
    },

    linkedAppointment() {
      if (!get(this.currentSessionActivity, 'appointment')) {
        return null;
      }
      return find(this.clientAppointmentsInProgress, { id: this.currentSessionActivity.appointment }) || null;
    },

    userCanAccessSession() {
      return (this.activeSession.sessionType === 'training')
        || !this.activeSession.limitedVisibility
        || this.hasSupervisorCredentials
        || this.activeSession.visibility.includes(this.userDetails.userPrivilege || this.userDetails.role);
    }, // userCanAccessSession()

    canParticipate() {
      return !this.isCurrentSessionOwner && !this.isCurrentSessionSupervisor;
    },

    isParticipating() {
      return this.isCurrentSessionOwner
        && (get(this.activeSession, 'originalRunBy', null) !== null);
    },

    showMediaButtons() {
      return !['asr'].includes(get(this.currentProgram, 'type'));
    }, // showMediaButtons()

    isIosBrowser() {
      return this.$q.platform.is.ios && !this.$q.platform.is.capacitor;
    }, // isIosBrowser()

    currentSessionActivity() {
      const processedSessionActivities = !this.showBackupData
        ? this.sessionActivities
        : this.processedActivities;

      return chain(processedSessionActivities)
        .filter({ type: 'session' })
        .filter({ status: 'in-progress' })
        .orderBy('createdAt')
        .last()
        .value();
    }, // currentSessionActivity()

    currentBehaviorProgram() {
      if (!this.isGroupSession || (this.activeSession?.client === this.currentClient.id)) {
        return chain(this.programs)
          .filter({ type: 'behaviour' })
          .head()
          .value() || null;
      }
      return chain(this.cachedGroupPrograms)
        .get(this.activeSession?.client)
        .filter({ type: 'behaviour' })
        .head()
        .value() || null;
    }, // currentBehaviorProgram()

    // toggle note in sessionActivity to display it from backup or from activities collection
    processedActivities() {
      const notesFromBackup = this.backupNotes;

      return map(this.sessionActivities, (activity) => {
        if (!has(notesFromBackup, activity.id)) {
          return activity;
        }

        const notesData = get(notesFromBackup, activity.id);

        if (activity.notes || notesData?.notes) {
          return {
            ...activity,
            notes: notesData.notes,
            id: activity.id,
          };
        }

        return {
          ...activity,
          sessionNotesFieldData: notesData.fieldsData,
          id: activity.id,
        };
      });
    }, // processedSessionActivity()

    isOwnerOfCurrentSessionActivity() {
      return get(this.currentSessionActivity, 'user') === this.userDetails.userId;
    },

    hasCurrentSessionActivity() {
      return !isEmpty(this.currentSessionActivity);
    },

    showNotesSection() {
      return !['asr'].includes(get(this.currentProgram, 'type'));
    }, // showNotesSection()

    canShowSessionNotes() {
      return (!['training'].includes(get(this.activeSession, 'sessionType')));
    }, // canShowSessionNotes

    acceptCallsImmediately() {
      return this.isCurrentSessionOwner
        && get(this.activeSession, 'autoAcceptVideoCalls', false);
    }, // acceptCallsImmediately()

    programNotes: {
      get() {
        if (!this.currentSessionProgram || !this.currentSessionProgram?.id) {
          return '';
        }
        return this.editProgramNotes;
      },
      set(v) {
        if (!this.currentSessionProgram?.id) {
          return;
        }

        this.editProgramNotes = v;
      },
    }, // programNotes

    editProgramNotesInitialValue() {
      if (!this.currentSessionProgram || !this.currentSessionProgram?.id) {
        return '';
      }
      return get(this.sessionProgramsData, [this.currentSessionProgram?.id, 'notes'], '');
    }, // editProgramNotesInitialValue()

    canUserInviteToCall() {
      return this.hasTherapistCredentials && this.isCurrentSessionOwner;
    }, // canUserInviteToCall()

    canUserJoinVideoCall() {
      return this.hasTherapistCredentials && !this.isCurrentSessionOwner;
    }, // canUserJoinVideoCall()

    intervalProgramsData() {
      return pickBy(this.sessionProgramsData, (programData) => get(programData, ['dataType']) === 'Interval1.0');
    },

    hasIntervalProgram() {
      return !isEmpty(this.intervalProgramsData);
    },

    sessionNotesButtonColor() {
      if (this.isBackupDifferent && !this.offlineMode && this.networkOnline && !this.isSavingSessionNotes) {
        return 'negative';
      }

      return this.showSessionNotes ? 'primary-variant' : 'primary';
    }, // sessionNotesButtonColor()

    // This computed is to facilitate the process of watching changes on `this.data[n].data`
    dataAsString() {
      if (!this.data || isEmpty(this.data)) {
        return '';
      }

      return this.data.reduce((acc, dataDoc) => {
        return `${acc}-${dataDoc.sessionProgram}_${dataDoc?.data?.length || 0}`;
      }, '');
    },

    hiddenRoles() {
      const { limitedVisibility, visibility } = this.activeSession;

      if (!limitedVisibility) {
        return [];
      }

      if (!visibility.length) {
        return this.visibilityOptions.map((item) => item.value);
      }

      return this.visibilityOptions
        .filter(({ value }) => !visibility.includes(value))
        .map((item) => item.value);
    },

    hiddenUsers() {
      const hiddenUsers = this.sessionActivities.filter(({ type, status }) => {
        return type === 'supervision' && status === 'in-progress';
      });

      if (!hiddenUsers.length) {
        return [];
      }

      return hiddenUsers.map(({ user }) => user);
    },

    parentTargets() {
      const activeProgramsTargets = [];
      this.activeSessionPrograms.forEach((sessionProgram) => {
        return activeProgramsTargets.push(...(sessionProgram.program?.targets || []));
      });
      return activeProgramsTargets.reduce((acc, target) => {
        if (target.parentTarget && !(target.parentTarget in acc)) {
          acc[target.parentTarget] = activeProgramsTargets.find(({ id }) => id === target.parentTarget);
        }
        return acc;
      }, {});
    },
  },

  watch: {

    activeSessionProgram: {
      handler(newId) {
        this.editProgramNotes = newId
          ? get(this.sessionProgramsData, [newId, 'notes'], '')
          : '';
      },
    },

    editProgramNotesInitialValue: {
      immediate: true,
      handler(value) {
        this.editProgramNotes = value;
      },
    },

    groupSessionClientIds: {
      immediate: true,
      deep: true,
      handler(groupSessionClientIds) {
        this.bindGroupPrograms({ groupSessionClientIds });
      },
    },

    currentSessionActivity: {
      immediate: true,
      deep: true,
      handler(newSessionActivity) {
        // We don't need to check for the backup data mismatch during the notes saving
        if (this.isSavingSessionNotes) {
          return;
        }

        // Compare session notes from the session activity and from the backup
        this.checkSessionNotesBackupDifference(newSessionActivity);
      },
    },

    'currentSessionActivity.user': {
      immediate: true,
      deep: false,
      handler(newSessionActivityUser) {
        // Log geolocation for the start of the session
        if (get(this.currentSessionActivity, 'status') !== 'in-progress') {
          return;
        }
        if ((newSessionActivityUser === this.userDetails.userId)
          && (get(this.currentSessionActivity, 'startGeolocation') === null)) {
          this.saveSessionActivityGeolocation('startGeolocation', this.currentSessionActivity.id);
        }
      },
    },

    activeSession: {
      immediate: false,
      deep: true,
      handler(newSession, oldSession) {
        // Handle changes in who is in charge of data collection for this session
        const newRunBy = get(newSession, 'runBy', null);
        const oldRunBy = get(oldSession, 'runBy', null);

        if ((newSession?.id === oldSession?.id)
          && (oldRunBy && newRunBy && (newRunBy !== oldRunBy))) {
          if (newRunBy === this.userDetails.userId) {
            this.notifySuccess(this.$t('run.collectData.notifyNewParticipantSelf'));
          } else {
            this.notifySuccess(
              this.$t(
                'run.collectData.notifyNewParticipant',
                { userName: get(newSession, 'runByName', null) },
              ),
            );
          }
        }

        if (!oldRunBy
          || !newRunBy
          || (oldRunBy === newRunBy)
          || (get(newSession, 'id', null) !== get(oldSession, 'id', null))) {
          return;
        }

        if (newRunBy === this.userDetails.userId) {
          this.bindSessionActivities();
        } else if (oldRunBy === this.userDetails.userId) {
          this.showSessionNotes = false;
        }
      },
    },

    'activeSession.requestControl': {
      immediate: true,
      deep: true,
      handler(request) {
        const userId = get(request, 'userId', null);
        const userName = get(request, 'userName', null);
        if (!userId
          || (!this.isCurrentSessionOwner)
          || (userId === this.userDetails.userId)
        ) {
          return;
        }
        this.handOverControlRequest(userId, userName);
      },
    },

    'activeSession.status': {
      immediate: false,
      async handler(newVal, oldVal) {
        if (!this.preventEndedByOtherMessage && oldVal && ([null, 'processing', 'done'].includes(newVal))) {
          if (this.isGroupSession) {
            // Find the next session to switch to if the current session is finished
            const newSessionId = chain(this.visibleGroupSessions)
              .map('id')
              .head()
              .value();
            if (newSessionId) {
              await this.switchGroupSession(newSessionId);
              return;
            }
          }

          // Pause the session to stop the session timer and all intervals.
          const sessionsDb = new SessionsDB();
          await sessionsDb.set({ durationIsPaused: true }, this.activeSession.id);
          this.gotoHomeScreen(
            this.$t('run.collectData.dialogSessionFinishMessage'),
            null,
            async () => {
              if (this.activeSession?.id) {
                await sessionsDb.set({ durationIsPaused: false }, this.activeSession.id);
              }
            },
          );
        }
      },
    },

    'activeSession.client': {
      immediate: false,
      handler(newClient, oldClient) {
        if (newClient !== oldClient) {
          this.invalidateBehaviourDataCache();
        }
      },
    },

    'activeSession.currentProgram': {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.activeSessionProgram) {
          this.activeSessionProgram = newVal;
        }
      },
    },

    hasIntervalProgram: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isIosBrowser) {
          this.openComponentDialog(
            IntervalSoundConfirm,
            {
              intervalPrograms: this.intervalProgramsData,
            },
            (audioMap) => {
              this.iosIntervalAudios = audioMap;
            },
          );
        }
      },
    },

    // watch changes of `this.data[n].data` array
    dataAsString: {
      immediate: true,
      deep: true,
      handler() {
        if (!this.data) {
          return;
        }

        this.data.forEach((dataDoc) => {
          const lastTrialData = maxBy(dataDoc.data, 'trial');
          this.cacheTrialNumber(dataDoc.sessionProgram, lastTrialData?.trial || 0);
        });
      },
    },
  },

  mounted() {
    this.orientationListener = this.checkOrientation;
    window.addEventListener('resize', this.orientationListener);
    window.addEventListener('orientationchange', this.orientationListener);

    this.$nextTick(() => {
      if (this.$refs.header) {
        this.headerResizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
        this.headerResizeObserver.observe(this.$refs.header?.$el || this.$refs.header);
      }
    });
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.orientationListener);
    window.removeEventListener('orientationchange', this.orientationListener);

    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
      this.headerResizeObserver = null;
    }
  },

  methods: {
    ...mapActions(programs, ['bindGroupPrograms', 'unbindGroupPrograms']),
    ...mapActions(sessions, [
      'switchActiveSession',
      'finishActiveSession',
      'refreshSessionProgramPrograms',
      'clearGroupSessionPrograms',
      'getActiveVideoSession',
      'isRunByInGroupSession',
    ]),
    ...mapActions(programBehaviourData, ['invalidateBehaviourDataCache']),
    ...mapActions(programsTargets, ['invalidateProgramTargetsCache']),
    ...mapActions(sessions, ['bindVideoCallSessions', 'unbindVideoCallSessions']),

    isBehaviorTrackingProgram,

    // A pinned program shows no name, so its info button is what marks the program type.
    getPinnedProgramIcon(program) {
      return isBehaviorTrackingProgram(program) ? BEHAVIOR_TRACKING_ICON : 'info';
    }, // getPinnedProgramIcon()

    async onSwitchGroupSession(sessionId) {
      this.$q.loading.show();
      await this.switchGroupSession(sessionId);
      this.$q.loading.hide();
    },

    onRefreshProgramsAndTargets(defaultProgram = 'first') {
      this.refreshProgramsAndTargets(defaultProgram);
    }, // onRefreshProgramsAndTargets()

    async onRecordData(sessionProgram, target, data) {
      if (!get(sessionProgram, 'id') || !this.isCurrentSessionOwner) {
        return;
      }
      if (!isEmpty(this.groupSessions)) {
        const isRunByCurrentUser = await this.isRunByInGroupSession();
        if (!isRunByCurrentUser) {
          return;
        }
      }
      try {
        const trialData = this.prepareTrialData(sessionProgram, target, data, true);
        this.cacheTrialNumber(sessionProgram.id, trialData.trial);

        if (await this.saveData(sessionProgram, {}, [trialData])) {
          this.trackBehaviorDataRecorded(sessionProgram, {
            context: BEHAVIOR_DATA_CONTEXTS.INDEPENDENT,
            entryCount: 1,
          });
        }
      } catch (err) {
        this.cacheTrialNumber(sessionProgram.id, this.getCachedTrialNumber(sessionProgram.id) - 1);
        this.currentTrial[sessionProgram.id] -= 1;
        logEvent(err, {
          context: {
            sessionInfo: {
              sessionProgram,
              sessionProgramsData: this.sessionProgramsData,
            },
          },
        });

        console.error(err);
        // @todo reconsider using this dialog message because error might not be related to unstable connection
        this.$q
          .dialog({
            title: this.$t('run.collectData.dialogLostConnectionTitle'),
            message: this.$t('run.collectData.dialogLostConnectionMessage'),
            cancel: true,
          })
          .onOk(async () => {
            window.location.reload();
          });

        logEvent(err);
      }
    }, // onRecordData()

    onRecordMultipleData(sessionProgram, dataArr, target = null) {
      if (!get(sessionProgram, 'id') || !this.isCurrentSessionOwner) {
        return;
      }
      try {
        const preparedData = map(
          dataArr,
          (data) => (
            this.prepareTrialData(
              sessionProgram,
              target || chain(sessionProgram).get(['program', 'targets']).find({ id: data.target }).value(),
              data,
            )
          ),
        );

        return this.saveData(sessionProgram, {}, preparedData).then((saved) => {
          if (saved) {
            // Phantom trials are zero-frequency placeholders added at session end, not recorded data.
            const recorded = preparedData.filter(({ phantomTrial }) => !phantomTrial).length;
            this.trackBehaviorDataRecorded(sessionProgram, {
              context: BEHAVIOR_DATA_CONTEXTS.INDEPENDENT,
              entryCount: recorded,
            });
          }
          return saved;
        });
      } catch (err) {
        logEvent(err, {
          context: {
            sessionInfo: {
              sessionProgram,
              sessionProgramsData: this.sessionProgramsData,
            },
          },
        });

        console.error(err);
        // @todo reconsider using this dialog message because error might not be related to unstable connection
        this.$q
          .dialog({
            title: this.$t('run.collectData.dialogLostConnectionTitle'),
            message: this.$t('run.collectData.dialogLostConnectionMessage'),
            cancel: true,
          })
          .onOk(async () => {
            window.location.reload();
          });

        logEvent(err);
      }
    }, // onRecordMultipleData()

    /**
     * Stores behavior occurrences on their Behavior Tracking programs' own data documents,
     * numbered in sequence the way onRecordData numbers trials. Provided to the targets as
     * `recordBehaviorOccurrences`.
     */
    async onRecordBehaviorOccurrences({ occurrences, attachedToTarget, skillProgramId = null }) {
      if (!this.isCurrentSessionOwner) {
        return;
      }
      if (!isEmpty(this.groupSessions) && !(await this.isRunByInGroupSession())) {
        return;
      }

      const occurrenceDataByProgram = toBehaviorOccurrenceData(occurrences, attachedToTarget);

      const results = await Promise.allSettled(Object.entries(occurrenceDataByProgram)
        .map(async ([programId, occurrenceData]) => {
          const sessionProgram = this.activeSessionPrograms
            .find((candidate) => candidate.program?.id === programId);
          if (!sessionProgram) {
            return;
          }
          const targets = sessionProgram.program.targets ?? [];
          const trialBefore = this.getCachedTrialNumber(sessionProgram.id);

          try {
            const entries = occurrenceData
              .map((data) => ({ data, target: targets.find(({ id }) => id === data.target) }))
              // A target removed mid-session has nowhere to be recorded.
              .filter(({ target }) => target)
              .map(({ data, target }) => {
                const entry = this.prepareTrialData(sessionProgram, target, data, true);
                this.cacheTrialNumber(sessionProgram.id, entry.trial);
                return entry;
              });

            if (entries.length === 0) {
              return;
            }
            if (!(await this.saveData(sessionProgram, {}, entries))) {
              // Nothing was written, so the next occurrence reuses these trial numbers.
              this.cacheTrialNumber(sessionProgram.id, trialBefore);
              return;
            }
            this.trackBehaviorDataRecorded(sessionProgram, {
              context: BEHAVIOR_DATA_CONTEXTS.WITHIN_SKILL_ACQUISITION,
              entryCount: entries.length,
              skillProgramId,
              skillTargetId: attachedToTarget,
            });
          } catch (err) {
            this.cacheTrialNumber(sessionProgram.id, trialBefore);
            logEvent(err, { context: { sessionInfo: { sessionProgram } } });
            console.error(err);
            // Rethrown so allSettled reports it and one dialog covers every failed program.
            throw err;
          }
        }));

      if (results.some(({ status }) => status === 'rejected')) {
        this.$q.dialog({
          title: this.$t('run.collectData.dialogLostConnectionTitle'),
          message: this.$t('run.collectData.dialogLostConnectionMessage'),
          cancel: true,
        })
          .onOk(() => window.location.reload());
      }
    }, // onRecordBehaviorOccurrences()

    trackBehaviorDataRecorded(sessionProgram, {
      context, entryCount, skillProgramId = null, skillTargetId = null,
    }) {
      if (!isBehaviorTrackingProgram(sessionProgram.program)) {
        return;
      }

      behaviorTrackingAnalytics.trackDataRecorded({
        programId: sessionProgram.program.id,
        sessionId: this.activeSession?.id ?? null,
        context,
        entryCount,
        skillProgramId,
        skillTargetId,
      });
    }, // trackBehaviorDataRecorded()

    async onRecordProgramData(sessionProgram, programData) {
      if (!get(sessionProgram, 'id') || (!this.isCurrentSessionOwner)) {
        return;
      }
      if (!isEmpty(this.groupSessions)) {
        const isRunByCurrentUser = await this.isRunByInGroupSession();
        if (!isRunByCurrentUser) {
          return;
        }
      }

      const dataId = get(this.sessionProgramsData[sessionProgram.id], 'id', null);
      if (dataId) {
        const dataDb = new DataDB();
        const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);

        const backupDataObj = {
          ...this.sessionProgramsData[dataId],
          ...programData,
          id: `backupData_${dataId}`,
        };

        await this.setSessionBackup(sessionBackupDataDb, backupDataObj);
        await dataDb.set(programData, dataId);
      } else {
        this.$q.dialog({
          title: this.$t('run.collectData.errorDataNotSavedTitle'),
          message: this.$t('run.collectData.errorDataNotSavedMessage'),
          cancel: false,
          persistent: true,
        }).onOk(this.recreateMissingDataDocumentsBySessionPrograms);
      }
    }, // onRecordProgramData()

    onEditProgramNotes(sessionProgram) {
      const notes = get(this.sessionProgramsData[sessionProgram.id], 'notes', null);
      this.openComponentDialog(
        EditNotes,
        {
          title: sessionProgram.programName,
          value: notes,
          defaultValue: '',
          plainText: true,
          label: this.$t('run.collectData.inputProgramNotesLabel'),
        },
        async (newProgramNotes) => {
          if (!this) {
            // Module already destroyed
            return;
          }
          this.saveData(sessionProgram, { notes: newProgramNotes });
        },
      );
    },

    async onPauseSessionTimer(duration, showDialog = true) {
      if (!this.isCurrentSessionOwner) {
        return;
      }

      if (!isEmpty(this.groupSessions)) {
        const isRunByCurrentUser = await this.isRunByInGroupSession();
        if (!isRunByCurrentUser) {
          return;
        }
      }

      // Save session duration
      const pausedAt = Timestamp.now();
      this.sessionPauseGeneration += 1;
      const pauseGeneration = this.sessionPauseGeneration;
      const sessionsDb = new SessionsDB();
      // Not awaited before the group pass: offline, the ack waits for the reconnect. HR-4624
      const activeSessionWrite = sessionsDb.set(
        {
          duration,
          durationOffsetTimestamp: null,
          durationIsPaused: true,
          // Marked like the other clients are, because by the time the pause is lifted another client
          // of the group may be the active one and resumeGroupSessionTimers() selects on this. HR-4624
          sessionPauseFrozenAt: pausedAt,
          sessionPauseFrozenOffsets: frozenSessionOffsetFields(this.activeSession),
        },
        this.activeSession.id,
      );

      // The other clients in a group session are unmounted, so nothing freezes their timers -
      // freeze them here or the pause is silently added to their data on the next mount. HR-4624
      await sessionsDb.pauseGroupSessionTimers(this.groupSessions, {
        activeSessionId: this.activeSession.id,
        userId: this.userDetails.userId,
        pausedAt,
        isSuperseded: () => pauseGeneration !== this.sessionPauseGeneration,
      });
      await activeSessionWrite;

      if (showDialog) {
        this.showPauseDialog();
      }
    }, // onPauseSessionTimer()

    onShowPauseDialog() {
      this.showPauseDialog();
    },

    onFinishFrequencyTimer(sessionProgram, duration) {
      if (!this.isCurrentSessionOwner) {
        return;
      }
      const dataObj = {
        duration,
        timerOffsetTimestamp: null,
      };
      this.saveData(sessionProgram, dataObj);
    }, // onFinishFrequencyTimer()

    onUndoData(sessionProgram) {
      if (this.sessionProgramsData[sessionProgram.id].data.length > 0) {
        const undoDataItem = this.sessionProgramsData[sessionProgram.id].data.at(-1);
        const target = sessionProgram?.program?.targets?.find(({ id }) => id === undoDataItem.target);

        const undoDataFunc = chain(this.$refs)
          .get(`sessionProgram_${sessionProgram.id}`)
          .head()
          .get('undoData')
          .value();
        if (isFunction(undoDataFunc)) {
          const continueWithUndo = undoDataFunc(undoDataItem);
          if (!continueWithUndo) {
            return;
          }
        }

        const prevPromptLevelId = get(undoDataItem, 'currentPromptLevel');
        if (prevPromptLevelId) {
          if (get(this.currentPromptLevel(sessionProgram, target), 'id') !== prevPromptLevelId) {
            this.updateTargetProperties({
              sessionProgram,
              target,
              targetProperties: { currentPromptLevel: prevPromptLevelId },
            });
            this.sortedTargets[sessionProgram.id] = this.refreshSortedTargets(sessionProgram);
          }
        }

        let undoDataItems = [undoDataItem];

        if (target.dataType === 'rate') {
          const isClosingTrial = ['time', 'duration'].every((key) => key in undoDataItem)
            && !['scoreValue', 'promptLevel'].some((key) => key in undoDataItem);

          if (isClosingTrial) {
            // If this is a closing rate target, then it is necessary to roll back all trials of this number
            undoDataItems = this.sessionProgramsData[sessionProgram.id].data.filter((trial) => {
              return trial.trial === undoDataItem.trial;
            });
          }
        }

        // Show notification
        this.notifyWarning(this.$t('run.collectData.notifyUndoData'), { html: true });
        this.saveData(sessionProgram, {}, [], undoDataItems);
      }
    }, // onUndoData()

    onInputProgramNotes() {
      this.saveData(this.currentSessionProgram, { notes: this.editProgramNotes });
    }, // onInputProgramNotes()

    /**
     * Compare session notes from activities collection and from indexedDB
     * according to the note source - created from the note template or manually added
     *
     * @param {Object} sessionActivity - current session activity used
     */
    async checkSessionNotesBackupDifference(sessionActivity) {
      const backupSessionNotes = this.getBackupNotes(sessionActivity?.id);

      if (isEmpty(backupSessionNotes) || backupSessionNotes.isBackupAddedOnline) {
        this.isBackupDifferent = false;
        return;
      }

      const sessionActivityNotes = chain(this.sessionActivities)
        .find({ id: get(sessionActivity, 'id') })
        .get('notes')
        .value();

      const hasActiveTemplateAndNotesFields = (
        this.hasActiveNoteTemplate && has(sessionActivity, 'sessionNotesFieldData')
      );

      const fieldsToIgnore = chain(['sessionDuration', 'sessionFinishTime', 'sessionStartTime'])
        .map((field) => {
          if (has(get(backupSessionNotes, ['fieldsData', 'session']), field)) {
            return [field, backupSessionNotes.fieldsData.session[field]];
          }

          return null;
        })
        .compact()
        .fromPairs()
        .value();

      const notesFromActivity = this.handledActivitySessionTimeFields(sessionActivity, fieldsToIgnore);

      const isDifferentTemplateNotes = !isEqual(
        get(notesFromActivity, 'sessionNotesFieldData'),
        backupSessionNotes.fieldsData,
      );

      const isDifferentTextNotes = isString(backupSessionNotes.notes)
          && (backupSessionNotes.notes !== sessionActivityNotes);

      if ((hasActiveTemplateAndNotesFields && !sessionActivityNotes && isDifferentTemplateNotes)
        || (this.hasActiveNoteTemplate && !!sessionActivityNotes && isDifferentTextNotes)
        || (isDifferentTextNotes)) {
        this.isBackupDifferent = true;
        return;
      }

      this.isBackupDifferent = false;
    }, // checkSessionNotesBackupDifference()

    handledActivitySessionTimeFields(activity, fieldsToIgnore) {
      const sessionFields = {
        ...get(activity, ['sessionNotesFieldData', 'session']),
        ...fieldsToIgnore,
      };

      return {
        ...activity,
        sessionNotesFieldData: {
          ...get(activity, 'sessionNotesFieldData'),
          ...(has(activity, ['sessionNotesFieldData', 'session']) && { session: sessionFields }),
        },
      };
    }, // handledActivitySessionTimeFields()

    async onAutomationActionCompleted(automationActionResults) {
      if (!chain(automationActionResults).get('programs').isEmpty().value()) {
        await this.refreshSessionProgramPrograms({ programIds: automationActionResults.programs });
      }
      if (automationActionResults?.currentSessionProgram) {
        this.gotoSessionProgram(automationActionResults.currentSessionProgram);
      }
      if (automationActionResults?.finishSession) {
        this.finishSession('done');
      }
    }, // onAutomationActionCompleted()

    onShowBackupNotes(evt) {
      this.showBackupData = evt;
    }, // onShowBackupNotes()

    async onSaveSessionActivityNotes(sessionActivity, changes) {
      const status = this.getNewSessionActivityStatus(sessionActivity, changes);

      const changedActivityNotes = {
        ...sessionActivity,
        ...changes,
        status,
        id: sessionActivity.id,
        sessionNotesFieldData: get(changes, 'sessionNotesFieldData'),
      };
      this.isSavingSessionNotes = true;
      try {
        const sessionActivitiesDb = new SessionActivitiesDB();
        const actionsPromises = [this.backupSessionActivityNotes(changedActivityNotes)];
        if (this.offlineMode || this.networkOnline) {
          actionsPromises.push(sessionActivitiesDb.update({ ...changes, status, id: sessionActivity.id }));
        }
        await AppUtils.allSettled(actionsPromises);
      } finally {
        this.showBackupData = false;
        this.isSavingSessionNotes = false;
      }
    }, // onSaveSessionActivityNotes()

    noteTemplateSelected({ hasNoteTemplate }) {
      this.hasActiveNoteTemplate = hasNoteTemplate;
    }, // noteTemplateSelected()

    async backupSessionActivityNotes(sessionActivity) {
      if (sessionActivity) {
        const currentSessionNotesFieldData = get(sessionActivity, 'sessionNotesFieldData');

        const aggregatedBackupData = {
          id: `backupNotes_${sessionActivity.id}`,
          session: sessionActivity.session,
          client: sessionActivity.client,
          notes: get(sessionActivity, 'notes', ''),
          fieldsData: currentSessionNotesFieldData || null,
          isBackupAddedOnline: (this.networkOnline || this.offlineMode),
        };

        const sessionBackupNotesDb = new GenericIndexedDb(BACKUP_NOTES);
        await this.setSessionBackup(sessionBackupNotesDb, aggregatedBackupData);

        this.isBackupDifferent = false;
      }
    }, // backupSessionActivityNotes()

    onFinishSession() {
      if (!this.networkOnline && !this.offlineMode) {
        this.alertDialog(
          this.$t('common.noInternetConnection'),
          this.$t('run.collectData.badConnectionFinishSessionConfirmAlert'),
        );
        return;
      }

      if (this.isGroupSession) {
        this.finishGroupSessionDialog();
      } else {
        this.finishSessionDialog();
      }
    }, // onFinishSession()

    onExitSession() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('run.collectData.dialogExitSessionMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          try {
            this.$q.loading.show();
            if (!this) {
              // Module already destroyed
              return;
            }
            if (['training'].includes(get(this.activeSession, 'sessionType'))
              && !get(this.activeSession, 'durationIsPaused')) {
              await this.onPauseSessionTimer(this.$refs.sessionTimer.getTime(), false);
            }

            await this.finishVideoSession();
            this.unsavedChanges = false;
            this.preventEndedByOtherMessage = true;
            sessionStorage.removeItem(IS_INVITATION_ACCEPTED);
            this.$nextTick(() => {
              this.$router.push('/');
            });
          } catch (error) {
            this.notifyNegative(error.message);
            console.error(error.message);
          } finally {
            this.$q.loading.hide();
          }
        });
    }, // onExitSession()

    async onParticipate() {
      try {
        if (!this.canParticipate) {
          return;
        }

        const sessionChanges = {
          runBy: this.userDetails.userId,
          runByName: this.userDetails.name,
          isSessionActivityLocked: true,
        };
        if (get(this.activeSession, 'originalRunBy', null) === this.userDetails.userId) {
          assign(sessionChanges, {
            originalRunBy: null,
            originalRunByName: null,
          });
        } else if (get(this.activeSession, 'originalRunBy', null) === null) {
          assign(sessionChanges, {
            originalRunBy: this.activeSession.runBy,
            originalRunByName: this.activeSession.runByName,
          });
        }

        const sessionsDb = new SessionsDB();

        await sessionsDb.set(sessionChanges, this.activeSession.id);
      } catch (err) {
        this.notifyNegative(err?.message ?? err);
        console.error(err);
        logEvent(err);
      }
    },

    async onStopParticipation() {
      try {
        if (!this.isParticipating) {
          return;
        }

        const sessionsDb = new SessionsDB();

        await sessionsDb.set(
          {
            runBy: this.activeSession.originalRunBy,
            runByName: this.activeSession.originalRunByName,
            originalRunBy: null,
            originalRunByName: null,
            isSessionActivityLocked: true,
          },
          this.activeSession.id,
        );
      } catch (err) {
        this.notifyNegative(err?.message ?? err);
        console.error(err);
        logEvent(err);
      }
    },

    onRequestSessionControl() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('run.collectData.dialogRequestControlMessage', {
            sessionRunByName: get(this.activeSession, 'runByName', ''),
          }),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          if (!this) {
            // Module already destroyed
            return;
          }
          this.requestSessionControl();
          this.openComponentDialog(
            WaitForSessionControl,
            null,
            () => {
              if (!this) {
                // Module already destroyed
                return;
              }

              this.handOverControl(this.userDetails.userId, this.userDetails.name);
            },
            () => {
              if (!this) {
                // Module already destroyed
                return;
              }

              this.cancelSessionControlRequest();
            },
          );
        });
    }, // onRequestSessionControl()

    onSwitchSessionNotes() {
      this.showSessionNotes = !this.showSessionNotes;
      if (this.showSessionNotes) {
        this.$nextTick(() => {
          const { getScrollTarget, setVerticalScrollPosition } = scroll;
          const el = this.$refs.sessionNotes.$el;
          const target = getScrollTarget(el);
          const offset = el.offsetTop;
          const duration = 200;
          setVerticalScrollPosition(target, offset, duration);
        });
      }
    }, // onSwitchSessionNotes()

    async bindUnstartedGroupAppointments() {
      const groupSessionRefId = this.activeSessionGroup?.groupSessionRefId ?? null;

      if (this.isGroupSession && groupSessionRefId !== null) {
        const appointmentsDb = new AppointmentsDB();
        await appointmentsDb.bindAll(
          this,
          'unstartedGroupSessionAppointments',
          [
            ['clinic', '==', this.connectedClinic.id],
            ['groupSessionRefId', '==', groupSessionRefId],
            ['status', '==', 'scheduled'],
          ],
        );
      }
    }, // bindUnstartedGroupAppointments()

    unbindUnstartedGroupAppointments() {
      const appointmentsDb = new AppointmentsDB();
      return appointmentsDb.unbind(this, 'unstartedGroupSessionAppointments');
    }, // unbindUnstartedGroupAppointments()

    async initSession() {
      this.isLoading = true;
      const { sessionId } = this.$route.params;
      await this.loadSession(sessionId);
      await this.quickRefreshProgramsAndTargets();
      await this.bindUnstartedGroupAppointments();

      this.isLoading = false;
    }, // initSession()

    getAudioBySession(sessionId) {
      return get(this.iosIntervalAudios, sessionId, null);
    }, // getAudioBySession()

    prepareTrialData(sessionProgram, target, data, useCachedTrial = false) {
      if (!target) {
        throw new Error('Target missing...');
      }
      let promptLevelFadingData = {};

      const phantomTrial = get(data, 'phantomTrial', false);

      if (!phantomTrial) {
        this.introduceTarget(sessionProgram, target);
        // An occurrence is an event on its target, not a scored trial, so it must not fade prompt levels.
        if (!isBehaviorOccurrence(data)) {
          promptLevelFadingData = this.analyzePromptLevelFadingPerTrial(sessionProgram, target, data);
        }
      }
      const trial = useCachedTrial
        ? (this.getCachedTrialNumber(sessionProgram.id) + 1)
        : this.getTrial(sessionProgram);

      const baseData = phantomTrial
        ? {}
        : {
          trial,
          time: this.$refs.sessionTimer.getTime(),
        };
      // Only save user ID if user was not the one who init'd the data (to conserve space)
      const userData = (get(this.sessionProgramsData, [sessionProgram.id, 'user']) === this.userDetails.userId)
        ? {}
        : { user: this.userDetails.userId };

      const programStatus = sessionProgram?.program?.status;
      const programStatusData = (
        !programStatus
        || get(this.sessionProgramsData, [sessionProgram.id, 'programStatus']) === programStatus
      )
        ? {}
        : {
          programStatus,
        };

      return {
        ...baseData,
        target: target.id,
        parentTarget: target.parentTarget || null,
        uid: uid(),
        ...userData,
        ...programStatusData,
        ...promptLevelFadingData,
        ...data,
      };
    }, // prepareTrialData()

    requestSessionControl() {
      const sessionsDb = new SessionsDB();
      const requestSessionObj = {
        requestControl: {
          userId: this.userDetails.userId,
          userName: this.userDetails.name,
        },
      };
      sessionsDb.set(requestSessionObj, this.activeSession.id);
    }, // requestSessionControl()

    cancelSessionControlRequest() {
      const sessionsDb = new SessionsDB();
      const requestSessionObj = {
        requestControl: null,
      };
      sessionsDb.set(requestSessionObj, this.activeSession.id);
    }, // cancelSessionControlRequest()

    handOverControlRequest(userId, userName) {
      this.$q
        .dialog({
          title: this.$t('run.collectData.confirmSessionControl.title'),
          message: this.$t('run.collectData.confirmSessionControl.textCaption', {
            requestUserName: userName,
          }),
          ok: {
            color: 'primary',
            flat: true,
            label: this.$t('common.buttonYes'),
          },
          cancel: {
            color: 'secondary',
            flat: true,
            label: this.$t('common.buttonNo'),
          },
          persistent: true,
        })
        .onOk(async () => {
          if (!this) {
            // Module already destroyed
            return;
          }
          this.handOverControl(userId, userName);
        }).onCancel(() => {
          this.cancelSessionControlRequest();
        });
    }, // handOverControlRequest()

    handOverControl(userId, userName) {
      if (userId === get(this.activeSession, 'runBy')) {
        return;
      }

      const sessionsDb = new SessionsDB();
      const handoverSessionObj = {
        runBy: userId,
        runByName: userName,
        requestControl: null,
        appointment: null,
        isSessionActivityLocked: false,
        duration: this.$refs.sessionTimer.getTime(),
      };
      sessionsDb.set(handoverSessionObj, this.activeSession.id);
    }, // handOverControl()

    async quickRefreshProgramsAndTargets(defaultProgram = 'first') {
      try {
        await this.reloadSessionProgramWorkflows();
        if (!this.expandedView) {
          this.notifyProgramsWithInvalidWorkflow();
        }
      } finally {
        this.refreshAllSortedTargets();
        this.isLoading = false;
        this.$q.loading.hide();
        this.goToCurrentSessionProgram(defaultProgram);
      }
    }, // quickRefreshProgramsAndTargets()

    /**
     * Completely refresh all assets for the active session and jump to defaultProgram if we are not
     * already at a program.
     */
    async refreshProgramsAndTargets(defaultProgram = 'first') {
      this.$q.loading.show({ message: this.$t('run.collectData.messageUpdatingPrograms') });
      this.isLoading = true;

      try {
        const activeSessionId = this.activeSession.id || get(this.$route, ['params', 'sessionId'], null);
        this.invalidateProgramTargetsCache();
        await this.refreshFromOriginalSession();
        await this.closeVideoChat();

        await this.loadActiveSession({ sessionId: activeSessionId, forceUpdate: true });
        // We need to refresh sorted targets before getting program workflows just to prepare session programs
        // data with targets labels in on operation. Then we refresh sorted targets again to get ready targets.
        this.refreshAllSortedTargets();

        // We need to clear 'groupSessionPrograms' after refreshing programs to trigger
        // dispatching 'loadSessionPrograms' while switching client tabs in the group session
        await this.clearGroupSessionPrograms({ sessionId: this.activeSession.id });

        await this.reloadSessionProgramWorkflows(true);
      } finally {
        this.refreshAllSortedTargets();
        this.isLoading = false;
        this.$q.loading.hide();
        this.goToCurrentSessionProgram(defaultProgram);

        if (this.isInvitedUser) {
          this.isVideoCallCreated = false;
        }

        const isInvitationAccepted = sessionStorage.getItem(IS_INVITATION_ACCEPTED);
        const activeVideoSession = await this.getActiveVideoSession();
        const isHost = activeVideoSession?.runBy === this.userDetails.userId;

        if (!!activeVideoSession && (isInvitationAccepted || isHost)) {
          await this.createVideoCall(null, false);
        }
      }
    }, // refreshProgramsAndTargets()

    goToCurrentSessionProgram(defaultProgram = 'first') {
      if (isEmpty(this.currentProgram)) {
        const currentSessionProgram = (defaultProgram === 'last')
          ? last(this.availableSessionPrograms)
          : head(this.availableSessionPrograms);
        if (get(currentSessionProgram, 'id')) {
          this.gotoSessionProgram(currentSessionProgram.id);
        }
      } else if (this.expandedView) {
        this.notifyProgramsAndTargetsInvalidWorkflows();
      } else {
        this.notifyInvalidActiveProgramWorkflow(this.currentProgram.id);
        this.notifyInvalidTargetWorkflow(this.currentProgram);
      }
    }, // goToCurrentSessionProgram()

    canUndo(sessionProgram) {
      return this.isCurrentSessionOwner
        && (this.getTrial(sessionProgram) > 1)
        && (!['interval', 'esdm', 'instructions', 'asr'].includes(
          get(sessionProgram, ['program', 'type']),
        )
        );
    }, // canUndo

    async endSession(sessionStatus = 'processing') {
      if (this.isOwnerOfCurrentSessionActivity) {
        this.refreshGeolocation(false);
        if (this.evvRequired) {
          // Make sure we don't finish the session until we have the geolocation if
          // EVV is required
          await this.saveSessionActivityGeolocation('endGeolocation', get(this.currentSessionActivity, 'id'));
        } else {
          this.saveSessionActivityGeolocation('endGeolocation', get(this.currentSessionActivity, 'id'));
        }
      }

      const sessionsDb = new SessionsDB();
      await sessionsDb.endSession(
        this.activeSession,
        this.connectedClinic,
        this.sessionDuration,
        sessionStatus,
        'normal',
      );
      try {
        await this.refreshLumaryAppointments(this.connectedClinic.integration, this.linkedAppointment);
      } catch (e) {
        console.error(e);
      }
    }, // endSession()

    async gotoSessionProgram(sessionProgramId) {
      if (!sessionProgramId) {
        return;
      }

      this.activeSessionProgram = sessionProgramId;

      if (this.isCurrentSessionOwner) {
        const sessionsDb = new SessionsDB();
        await sessionsDb
          .setCurrentProgram(this.activeSession.id, sessionProgramId);
      }
    }, // gotoSessionProgram()

    setupInactivityTracking() {
      if (this.activeSession.sessionType !== 'training'
        || this.inactivityTracker
        || !this.isCurrentSessionOwner) {
        return;
      }

      this.inactivityTracker = createInactivityTracking({
        isTrainingVideoPlaying: () => this.isTrainingVideoPlaying,
        isSessionTimerPaused: () => this.isSessionTimerPaused,
        getSessionTimerTime: () => this.$refs.sessionTimer?.getTime?.(),
        onPauseSessionTimer: async (time) => {
          await this.onPauseSessionTimer(time);
        },
      });

      this.inactivityTracker.setupInactivityTracking();
    },

    removeInactivityTracking() {
      if (!this.inactivityTracker) {
        return;
      }

      this.inactivityTracker.removeInactivityTracking();
      this.inactivityTracker = null;
    },

    showPauseDialog() {
      if (!this.isCurrentSessionOwner) {
        this.$q
          .dialog({
            title: this.$t('run.collectData.pauseDialog.dialogTitle'),
            message: '',
            ok: false,
          });
        return;
      }

      const causedByInactivity = this.inactivityTracker?.pauseCausedByInactivity;
      this.removeInactivityTracking();
      this.$q
        .dialog({
          title: this.$t('run.collectData.pauseDialog.dialogTitle'),
          message: causedByInactivity
            ? this.$t('run.collectData.pauseDialog.inactivityCaption') : '',
          ok: {
            label: this.$t('run.collectData.pauseDialog.buttonResume'),
          },
          persistent: true,
        }).onOk(() => {
          this.resumeSessionTimer(get(this.activeSession, 'duration', 0));
          this.setupInactivityTracking();
        });
    }, // showPauseDialog()

    async resumeSessionTimer(duration) {
      if (!this.isCurrentSessionOwner || !this.isSessionTimerPaused) {
        return;
      }

      const resumedAt = Timestamp.now();
      this.sessionPauseGeneration += 1;
      const resumeGeneration = this.sessionPauseGeneration;
      const sessionsDb = new SessionsDB();

      // Before the active session's pause flag lifts: mounted components react to it and must not
      // wake from an offset this pass has not rebased yet. HR-4624
      await sessionsDb.resumeGroupSessionTimers(this.groupSessions, {
        activeSessionId: this.activeSession.id,
        userId: this.userDetails.userId,
        resumedAt,
        isSuperseded: () => resumeGeneration !== this.sessionPauseGeneration,
      });

      // Save session duration
      await sessionsDb.set(
        {
          duration,
          durationOffsetTimestamp: resumedAt,
          durationIsPaused: false,
          // The sweep above only reaches the marker through `groupSessions`, which is empty in a
          // single-client session - clear it here so no stale freeze outlives this resume. HR-4624
          sessionPauseFrozenAt: null,
          sessionPauseFrozenOffsets: null,
        },
        this.activeSession.id,
      );
    }, // resumeSessionTimer()

    /**
     * Signal finish to all targets to allow them to clean up and save any unsaved data
     * */
    async sendFinishSignal() {
      if (this.$refs.sessionTimer) {
        const { duration } = this.$refs.sessionTimer.finishSession();

        if (this.isCurrentSessionOwner) {
          this.sessionDuration = duration;
        }
      }

      const finishProgramActions = chain(this.visibleSessionPrograms)
        .map(async (sessionProgram) => {
          const sessionProgramRef = chain(this.$refs)
            .get(`sessionProgram_${sessionProgram.id}`)
            .head()
            .defaultTo(null)
            .value();
          if (sessionProgramRef !== null) {
            const programData = sessionProgramRef.finishSession();

            const finalData = get(programData, 'finalData', []);
            const sessionData = get(programData, 'sessionData', {});
            const durationFromSession = get(programData, 'durationFromSession', false);
            if (!isEmpty(finalData)) {
              await this.onRecordMultipleData(sessionProgram, finalData);
            }
            if (durationFromSession) {
              sessionData.duration = this.sessionDuration;
            }
            if (chain(this.sessionProgramsData)
              .get([sessionProgram.id, 'data'])
              .find({ durationFromSession: true })
              .value()) {
              sessionData.duration = this.sessionDuration;
            }
            if (!isEmpty(sessionData)) {
              await this.onRecordProgramData(sessionProgram, sessionData);
            }
          }
        })
        .value();
      await Promise.all(finishProgramActions);
    }, // sendFinishSignal()

    async suspendSession() {
      try {
        this.$q.loading.show();

        if (this.isOwnerOfCurrentSessionActivity) {
          await this.refreshGeolocation(false);
          this.saveSessionActivityGeolocation('endGeolocation', get(this.currentSessionActivity, 'id'));
        }

        await this.sendFinishSignal();

        const sessionsDb = new SessionsDB();
        await sessionsDb.suspendSession(this.activeSession, this.sessionDuration);
        try {
          await this.refreshLumaryAppointments(this.connectedClinic.integration, this.linkedAppointment);
        } catch (e) {
          console.error(e);
        }

        this.unsavedChanges = false;
        this.gotoDoneScreen();
      } catch (err) {
        this.notifyNegative(err?.message ?? err);
        console.error(err);
        logEvent(err);
      } finally {
        this.$q.loading.hide();

        const realTimeNotificationsDb = new RealTimeNotificationsDB();
        realTimeNotificationsDb.create({
          clinic: this.connectedClinic.id,
          client: this.activeSession.client,
          context: RealTimeNotificationsDB.context.OFFLINE_MODE,
          isViewed: false,
          type: 'sessionStatusUpdated',
          viewedBy: [],
        });
      }
    },

    handoverSession() {
      this.openComponentDialog(
        SelectClientUser,
        {
          title: this.$t('run.collectData.handoverSessionDialog.title'),
          text: this.$t('run.collectData.handoverSessionDialog.text'),
          label: this.$t('run.collectData.handoverSessionDialog.label'),
          okButton: this.$t('run.collectData.handoverSessionDialog.okButton'),
          skipRoles: this.hiddenRoles,
          skipUsers: this.hiddenUsers,
        },
        async (user) => {
          if (isEmpty(user) || this.hiddenUsers.includes(user.user)) {
            return;
          }

          try {
            this.$q.loading.show();

            if (this.isOwnerOfCurrentSessionActivity) {
              this.refreshGeolocation(false);
              if (this.evvRequired) {
                // Make sure we don't finish the session until we have the geolocation if
                // EVV is required
                await this.saveSessionActivityGeolocation('endGeolocation', get(this.currentSessionActivity, 'id'));
              } else {
                this.saveSessionActivityGeolocation('endGeolocation', get(this.currentSessionActivity, 'id'));
              }
            }

            await this.sendFinishSignal();

            const sessionsDb = new SessionsDB();
            await sessionsDb.handoverSession(this.activeSession, user, this.sessionDuration);
            try {
              await this.refreshLumaryAppointments(this.connectedClinic.integration, this.linkedAppointment);
            } catch (e) {
              console.error(e);
            }

            this.unsavedChanges = false;
            this.gotoDoneScreen();
          } catch (err) {
            this.notifyNegative(err?.message ?? err);
            console.error(err);
            logEvent(err);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    },

    async finishSessionDialog() {
      const disableLeaveOpen = !this.userHasPrivilege('sessions/leaveOpen') || this.offlineMode;
      this.$q
        .dialog({
          title: this.$t('run.collectData.dialogFinishSessionTitle'),
          message: this.$t('run.collectData.dialogFinishSessionMessage'),
          cancel: true,
          persistent: true,
          class: 'finish-session-dialog',
          options: {
            type: 'radio',
            model: 'finish',
            items: [
              {
                label: this.$t('run.collectData.dialogFinishNowOption'),
                value: 'finish',
              },
              {
                label: this.$t('run.collectData.dialogFinishHandoverOption'),
                value: 'handover',
                disable: disableLeaveOpen,
              },
              {
                label: this.$t('run.collectData.dialogFinishSuspendOption'),
                value: 'suspend',
                disable: disableLeaveOpen,
              },
            ],
          },
        })
        .onOk(async (finishAction) => {
          try {
            this.$q.loading.show();
            if (!this) {
              // Module already destroyed
              return;
            }
            const sessionStatus = this.offlineMode ? 'offline-done' : undefined;

            await this.finishVideoSession(true);
            switch (finishAction) {
              case 'handover':
                await this.handoverSession();
                break;
              case 'suspend':
                await this.suspendSession();
                break;
              case 'finish':
              default:
                await this.finishSession(sessionStatus);
                break;
            }
          } catch (error) {
            this.notifyNegative(error.message);
            console.error(error.message);
          } finally {
            this.$q.loading.hide();
          }
        });
    }, // finishSessionDialog()

    async finishGroupSessionDialog() {
      const items = map(
        this.visibleGroupSessions,
        (session) => ({
          label: this.getClientProperty(session.client, 'alias')
                    || this.getClientProperty(session.client, 'clientName'),
          value: session.id,
          disable: session.runBy !== this.userDetails.userId,
        }),
      );
      const model = chain(items)
        .reject('disable')
        .map('value')
        .value();

      this.$q
        .dialog({
          title: this.$t('run.collectData.dialogFinishGroupSessionTitle'),
          message: this.$t('run.collectData.dialogFinishGroupSessionMessage'),
          cancel: true,
          persistent: true,
          class: 'finish-session-dialog',
          options: {
            type: 'checkbox',
            model,
            items,
            isValid: (val) => !isEmpty(val),
          },
        })
        .onOk(async (finishSessions) => {
          if (!this) {
            // Module already destroyed
            return;
          }
          const sessionStatus = this.offlineMode ? 'offline-done' : undefined;

          this.finishVideoSession(true);
          this.$q.loading.show({ message: this.$t('run.collectData.finishingSessionMessage') });
          // eslint-disable-next-line no-restricted-syntax
          for (const sessionId of finishSessions) {
            // eslint-disable-next-line no-await-in-loop
            await this.finishGroupSession(sessionId, sessionStatus);
          }
          if (isEmpty(this.visibleGroupSessions)) {
            this.gotoHomeScreen();
          } else {
            // Find the next session to switch to if the current session is finished
            const newSessionId = chain(this.visibleGroupSessions)
              .map('id')
              .reject((sessionId) => (includes(finishSessions, sessionId)))
              .head()
              .value();
            if (!newSessionId) {
              this.gotoHomeScreen();
            } else {
              await this.switchGroupSession(newSessionId);
            }
          }
          this.$q.loading.hide();
        });
    }, // finishSessionDialog()

    async finishGroupSession(sessionId, sessionStatus = 'processing') {
      if (this.activeSession?.id !== sessionId) {
        await this.switchGroupSession(sessionId);
        await this.$nextTick();
      }
      await this.sendFinishSignal();
      this.analyzePromptLevelFadingPerSession();
      await this.$nextTick();
      this.unsavedChanges = false;
      this.preventEndedByOtherMessage = true;

      // All done with this session
      try {
        await this.endSession(sessionStatus);
        await this.addActivity('sessionFinished');
      } catch (err) {
        this.$q.loading.hide();
        logEvent(err);
        this.notifyNegative(err.message);

        throw err;
      }
    }, // finishGroupSession()

    async finishSession(sessionStatus = 'processing') {
      const sessionType = get(this.activeSession, 'sessionType');

      this.$q.loading.show({ message: this.$t('run.collectData.finishingSessionMessage') });
      await this.sendFinishSignal();
      this.analyzePromptLevelFadingPerSession();
      await this.$nextTick();
      this.unsavedChanges = false;
      this.preventEndedByOtherMessage = true;

      // All done with this session
      try {
        await this.endSession(sessionStatus);
        this.addActivity('sessionFinished');
      } catch (err) {
        this.$q.loading.hide();
        logEvent(err);
        this.notifyNegative(err.message);

        throw err;
      } finally {
        if (sessionType === 'training') {
          this.gotoHomeScreen();
        } else {
          this.gotoDoneScreen();
        }
      }
    }, // finishSession()

    gotoDoneScreen() {
      if (this.hasCurrentSessionActivity) {
        return this.$router.push(`/run/${this.activeSession.id}/done/${this.currentSessionActivity.id}`);
      }
      this.$router.push(`/run/${this.activeSession.id}/done`);
    }, // gotoDoneScreen()

    gotoHomeScreen(message = null, title = null, onDismiss = null) {
      if ((message === null) || (this.preventEndedByOtherMessage)) {
        this.redirectToHomePage();
        return;
      }
      this.$q
        .dialog({
          title: title || this.$t('run.collectData.dialogSessionFinishTitle'),
          message,
          cancel: false,
          persistent: true,
        })
        .onDismiss(async () => {
          if (onDismiss) {
            await onDismiss();
          }
          this.redirectToHomePage();
        });
    }, // gotoHomeScreen()

    redirectToHomePage() {
      this.unsavedChanges = false;
      this.preventEndedByOtherMessage = true;
      this.$nextTick(() => {
        this.$router.push('/');
      });
    }, // redirectToHomePage

    async resumeStaffTrainingSession() {
      if (!this.isStaffTrainingSession || !this.isSessionTimerPaused) {
        return;
      }
      this.resumeSessionTimer(get(this.activeSession, 'duration', 0));
    },

    async loadSession(sessionId) {
      this.unsavedChanges = true;
      this.resetActivities();

      await this.loadActiveSession({
        sessionId, loadGroupSessions: true, forceUpdate: this.$route.query?.shouldForceUpdate,
      });
      if (this.activeSession?.id !== sessionId) {
        // Make sure we have the correct session
        this.switchActiveSession({ sessionId });
      }
      if (!this.userCanAccessSession) {
        this.preventSessionStart = true;
        this.gotoHomeScreen(this.$t('run.collectData.errorSessionNotAccessible'), this.$t('common.accessDenied'));
        return;
      }
      if (this.isSessionInactive) {
        this.preventSessionStart = true;
        return;
      }
      // We need to refresh sorted targets before getting program workflows just to prepare session programs
      // data with targets labels in on operation. Then we refresh sorted targets again to get ready targets.
      this.refreshAllSortedTargets();

      await this.loadData();
      await this.validateAndRefreshPromptLevels();
      await this.bindSessionActivities();
      await this.bindSessionFiles();
      this.refreshAllSortedTargets();
      this.resumeStaffTrainingSession();
    }, // loadSession()

    async switchGroupSession(sessionId) {
      if (!this.isGroupSession) {
        return;
      }
      const session = find(this.groupSessions, { id: sessionId });
      if (isEmpty(session) || (session.id === this.activeSession?.id)) {
        return;
      }
      this.unsavedChanges = true;
      this.isLoading = true;
      this.resetActivities();

      await this.switchActiveSession({ sessionId });
      if (!this.userCanAccessSession) {
        this.preventSessionStart = true;
        this.isLoading = false;
        this.gotoHomeScreen(this.$t('run.collectData.errorSessionNotAccessible'), this.$t('common.accessDenied'));
        return;
      }
      if (this.isSessionInactive) {
        // @todo Switch to first available session participant
        this.preventSessionStart = true;
        this.isLoading = false;
        return;
      }
      // We need to refresh sorted targets before getting program workflows just to prepare session programs
      // data with targets labels in on operation. Then we refresh sorted targets again to get ready targets.
      this.refreshAllSortedTargets();

      await this.loadData();
      await this.reloadSessionProgramWorkflows();
      await this.validateAndRefreshPromptLevels();
      this.goToCurrentSessionProgram('first');
      await Promise.all([
        this.bindSessionActivities(),
        this.bindSessionFiles(),
      ]);
      this.refreshAllSortedTargets();
      this.preventEndedByOtherMessage = false;
      this.isLoading = false;
    }, // switchGroupSession()

    async leaveGroupSession() {
      if (!this.activeSessionGroup) {
        return;
      }
      this.finishActiveSession();
    }, // leaveGroupSession()

    async setSessionBackup(backupDb, backupObj) {
      try {
        await backupDb.set(backupObj, backupObj.id);
      } catch (err) {
        if (!isQuotaExceededError(err)) {
          console.warn('Session backup failed', err);
          return;
        }
        if (!this.activeSession?.id) {
          notifyStorageQuotaExceeded();
          return;
        }
        try {
          // in a group session the other participants are still running, so their backups
          // are not stale and have to survive the cleanup
          await clearBackupsExceptCurrentSession([this.activeSession.id, ...map(this.groupSessions, 'id')]);
          await backupDb.set(backupObj, backupObj.id);
        } catch (retryErr) {
          console.warn('Session backup still failed after reclaiming storage space', retryErr);
          // the recovery did not help, so the user is the only one who can free up space now
          notifyStorageQuotaExceeded();
        }
      }
    }, // setSessionBackup()

    /**
     * @returns {Promise<boolean>} Whether the change was written — false when another user runs the
     *   group session or the data document is missing.
     */
    async saveData(sessionProgram, sessionProgramDataChanges = {}, addData = [], removeData = []) {
      if (!isEmpty(this.groupSessions)) {
        const isRunByCurrentUser = await this.isRunByInGroupSession();
        if (!isRunByCurrentUser) {
          return false;
        }
      }

      const dataId = get(this.sessionProgramsData[sessionProgram.id], 'id', null);
      if (!dataId) {
        this.$q.dialog({
          title: this.$t('run.collectData.errorDataNotSavedTitle'),
          message: this.$t('run.collectData.errorDataNotSavedMessage'),
          cancel: false,
          persistent: true,
        }).onOk(this.recreateMissingDataDocumentsBySessionPrograms);
        return false;
      }

      const dataDb = new DataDB();

      const dataObj = {
        id: dataId,
        ...sessionProgramDataChanges,
      };
      if (!isEmpty(addData)) {
        dataObj.data = arrayUnion(...addData);
      }
      if (!isEmpty(removeData)) {
        dataObj.data = arrayRemove(...removeData);
      }

      if (['interval', 'esdm'].includes(sessionProgram.program.type)) {
        dataObj.currentInterval = get(this.sessionProgramsData, [sessionProgram.id, 'currentInterval'], 1);
      }

      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);
      const backupDataObj = {
        ...this.sessionProgramsData[dataId],
        ...dataObj,
        id: `backupData_${dataId}`,
      };

      await this.setSessionBackup(sessionBackupDataDb, backupDataObj);
      await dataDb.update(dataObj);
      return true;
    }, // saveData

    async loadData() {
      await this.bindData();
      await this.bindBackupsData(this.activeSession.id);
      await this.bindBackupNotes(this.activeSession.id);

      if (!this.offlineMode) {
        const hasBackupDataDifference = await this.checkBackupData();

        if (!hasBackupDataDifference) {
          await this.syncBackupDataWithServerData();
        }
      }
    }, // loadData()

    async syncBackupDataWithServerData() {
      const sessionProgramsHaveData = some(this.sessionProgramsData, (programData) => {
        return Array.isArray(programData.data) && programData.data.length > 0;
      });

      if (!sessionProgramsHaveData) {
        return;
      }

      const sessionBackupDataDb = new GenericIndexedDb(BACKUP_DATA);

      const dataActions = map(this.sessionProgramsData, (sessionProgramData) => {
        const dataId = get(sessionProgramData, 'id', null);
        if (dataId) {
          const backupDataObj = {
            ...sessionProgramData,
            id: `backupData_${dataId}`,
          };

          return this.setSessionBackup(sessionBackupDataDb, backupDataObj);
        }
      });
      await Promise.all(dataActions);
    },

    async checkBackupData() {
      const ignoreProperties = ['updateTimestamp', 'createTimestamp', 'updateBy',
        'currentInterval', 'duration', 'timerOffsetTimestamp', 'durationPausedTimestamp',
        'durationIsPaused'];

      const mappedBackupData = chain(this.backupData)
        .pickBy({ session: this.activeSession.id })
        .mapValues(
          (sessionProgram) => (omit(sessionProgram, ignoreProperties)),
        )
        .value();

      const serverData = mapValues(
        this.sessionProgramsData,
        (sessionProgram) => (omit(sessionProgram, ignoreProperties)),
      );

      if (!isEqual(mappedBackupData, serverData)) {
        const backupDataSize = chain(mappedBackupData)
          .map('data')
          .reduce((pV, dataItem) => pV + size(dataItem), 0)
          .value();
        const serverDataSize = chain(serverData)
          .map('data')
          .reduce((pV, dataItem) => pV + size(dataItem), 0)
          .value();

        if (backupDataSize > serverDataSize) {
          logEvent(new Error('Mismatching backup data'), {
            context: {
              backupData: { mappedBackupData },
              serverData: { serverData },
            },
          });
          this.$q.loading.hide();
          this.preventSessionStart = true;
          this.$q.dialog({
            title: this.$t('run.collectData.restoreBackup.title'),
            message: this.$t('run.collectData.restoreBackup.message'),
            cancel: true,
            persistent: true,
          })
            .onOk(async () => {
              // Restore backup data
              const dataDb = new DataDB();
              const dataActions = map(this.backupData, (sessionProgramData) => {
                const dataId = get(sessionProgramData, 'id', null);
                if (dataId) {
                  return dataDb.set(omit(sessionProgramData, ['createTimestamp', 'updateTimestamp', 'id']), dataId);
                }
              });
              await Promise.all(dataActions);
            })
            .onDismiss(() => {
              this.preventSessionStart = false;
            });

          return true;
        }
      }

      return false;
    }, // checkBackupData()

    canEditProgramNotes(sessionProgram) {
      return this.isCurrentSessionOwner
        && !['instructions'].includes(get(sessionProgram, ['program', 'type']));
    }, // canEditProgramNotes

    /**
     * Analyze data to see if prompt level needs to be faded
     */
    analyzePromptLevelFadingPerTrial(sessionProgram, target, newData) {
      if (
        this.getTargetWorkflowDetails(
          this.workflowStatuses,
          sessionProgram.program,
          target,
          'type',
          this.parentTargets[target.parentTarget],
        ) !== 'acquisition'
      ) {
        return {};
      }

      const fadingStrategy = get(this.targetPromptLevelTemplate(sessionProgram, target), 'fadingStrategy');
      let fadingInfo = {};

      if (fadingStrategy === FADING_STRATEGIES.MTL) {
        fadingInfo = this.analyzePromptLevelFadingMostToLeast(
          sessionProgram,
          target,
          newData,
        );
      } else if (fadingStrategy === FADING_STRATEGIES.MTL_S) {
        fadingInfo = this.getPromptLevelScoreValue(
          sessionProgram,
          target,
          newData,
        );
      }

      if (!isEmpty(fadingInfo)) {
        this.sortedTargets[sessionProgram.id] = this.refreshSortedTargets(sessionProgram);
      }

      return fadingInfo;
    }, // analyzePromptLevelFadingPerTrial()

    /**
     * Analyze data to see if prompt level needs to be faded after session finishes
     */
    analyzePromptLevelFadingPerSession() {
      chain(this.sessionProgramsData)
        // Map out sessionPrograms and targets to analyze
        .map((sessionProgramData) => {
          const sessionProgram = find(this.activeSessionPrograms, { id: sessionProgramData.sessionProgram });
          const touchedTargets = chain(sessionProgramData)
            .get('data')
            .reject(isBehaviorOccurrence)
            .reject('parentTarget')
            .filter('promptLevel')
            .map((data) => (
              chain(this.sortedTargets)
                .get(sessionProgramData.sessionProgram)
                .find({ id: data.target })
                .defaultTo(null)
                .value()
            ))

            // Add Subtargets
            .concat(
              chain(sessionProgramData)
                .get('data')
                .reject(isBehaviorOccurrence)
                .filter('promptLevel')
                .filter('parentTarget')
                .map((data) => (
                  chain(this.sortedTargets)
                    .get(sessionProgramData.sessionProgram)
                    .find({ id: data.parentTarget })
                    .get('subTargets')
                    .find({ id: data.target })
                    .defaultTo(null)
                    .value()
                ))
                .value(),
            )

            .compact()
            .uniq()
            .reject(
              (target) => this.getTargetWorkflowDetails(
                this.workflowStatuses,
                get(sessionProgram, 'program'),
                target,
                'type',
              ) !== 'acquisition',
            )
            .value();

          return {
            sessionProgram,
            touchedTargets,
          };
        })
        // // Only process programs that was actually touched
        .reject((sessionProgramAndTargets) => chain(sessionProgramAndTargets)
          .get('touchedTargets')
          .isEmpty()
          .value())
        // Analyze targets
        .map((sessionProgramAndTargets) => chain(sessionProgramAndTargets)
          .get('touchedTargets')
          .filter(
            (target) => get(
              this.targetPromptLevelTemplate(
                sessionProgramAndTargets.sessionProgram,
                target,
              ),
              'fadingStrategy',
            ) === 'MTL-S',
          )
          .map((target) => this.analyzePromptLevelFadingMostToLeast(sessionProgramAndTargets.sessionProgram, target))
          .value())
        .value();
    }, // analyzePromptLevelFadingPerSession()

    /**
     * Check for prompt level fading when the strategy is Most to Least
     */
    analyzePromptLevelFadingMostToLeast(sessionProgram, target, newData = null) {
      let promptLevelFadingData = {};
      const targetPromptLevelTemplate = this.targetPromptLevelTemplate(sessionProgram, target);
      const targetPrompts = reject(
        this.targetPrompts(sessionProgram, target),
        { excludeFromPromptLevelFading: true },
      );
      let fadingDataChain = chain(this.getSessionProgramData(sessionProgram))
        .get('data')
        .filter({ target: target.id })
        // Occurrences are events on the target, not attempts at it, so they never drive its fading.
        .reject(isBehaviorOccurrence);
      if (newData !== null) {
        fadingDataChain = fadingDataChain
          .concat(newData);
        if (newData.errorPromptLevel === null) {
          // If the last trial was an error trial on the lowest prompt level, then we need don't need to continue
          return promptLevelFadingData;
        }
      }
      const fadingData = fadingDataChain
        // Treat error prompt levels as prompt levels in order to fade one step at a time
        .map((trial) => ({ ...trial, promptLevel: trial.errorPromptLevel || trial.promptLevel }))
        // Only include data with prompt levels that are included in prompt level fading
        .filter((trial) => find(targetPrompts, { id: trial.promptLevel }))
        .value();

      // Only analyze data if we have some data to analyze
      // and last trial was not a prompt level that was excluded from fading
      if (isEmpty(fadingData)) {
        return promptLevelFadingData;
      }
      if (newData !== null) {
        const lastTrialPromptLevel = find(this.targetPrompts(sessionProgram, target), { id: newData.promptLevel });
        if (lastTrialPromptLevel?.excludeFromPromptLevelFading && !lastTrialPromptLevel.isNegativeFadeResponse) {
          return promptLevelFadingData;
        }
      }

      //  First check if we should decrease prompt level
      const fadingDecreaseTrials = get(targetPromptLevelTemplate, 'fadingDecreaseTrials', 0);
      const fadingDecreaseSessions = get(targetPromptLevelTemplate, 'fadingDecreaseSessions', 1);
      const fadingDecreasePercentage = get(targetPromptLevelTemplate, 'fadingDecreasePercentage');
      const fadingIncreaseTrials = get(targetPromptLevelTemplate, 'fadingIncreaseTrials', 0);
      const fadingIncreaseSessions = get(targetPromptLevelTemplate, 'fadingIncreaseSessions', 1);
      const fadingIncreasePercentage = get(targetPromptLevelTemplate, 'fadingIncreasePercentage');
      const currentPromptLevel = this.currentPromptLevel(sessionProgram, target);
      const currentPromptLevelOrder = get(currentPromptLevel, 'order');

      if (newData !== null) {
        promptLevelFadingData = this.getPromptLevelScoreValue(sessionProgram, target, newData, targetPrompts);
      }
      // Only check if prompt level should be decreased if we are not already
      // on minimal prompting and we have enough data to check
      if ((currentPromptLevelOrder > 0)
        && (size(fadingData) >= fadingDecreaseTrials)) {
        // Prepare data for analyzing a decrease in current prompt level
        const decreaseFadingData = chain(fadingData)
          .takeRight(fadingDecreaseTrials || 999999)
          .map((trial) => (
            chain(targetPrompts)
              .find({ id: trial.promptLevel })
              .get('order')
              .value()
          ))
          .value();

        // Get prompt level order to decrease to
        const decreasePromptingToOrder = chain(decreaseFadingData)
          .uniq()
          .reject((promptLevelOrder) => promptLevelOrder > currentPromptLevelOrder)
          .map((promptLevelOrder) => ({
            promptLevelOrder,
            successRate: chain(decreaseFadingData)
              .reduce((sum, n) => (sum + ((n <= promptLevelOrder) ? 1 : 0)), 0)
              .divide(fadingDecreaseTrials || size(fadingData))
              .value(),
          }))
          .filter(
            (promptLevel) => Math.round(promptLevel.successRate * 100) >= fadingDecreasePercentage,
          )
          .minBy('promptLevelOrder')
          .get('promptLevelOrder')
          .defaultTo(null)
          .value();
        if (decreasePromptingToOrder !== null) {
          // Aim for one lower than currently reached prompt level
          const decreaseToPromptLevelId = chain(targetPrompts)
            .reject((promptLevel) => (promptLevel.order >= decreasePromptingToOrder))
            .last()
            .get('id')
            .defaultTo(chain(targetPrompts).head().get('id').value())
            .value();
          if (decreaseToPromptLevelId) {
            if ((get(target, 'fadingDecrease', 0) + 1) >= fadingDecreaseSessions) {
              this.updateTargetProperties({
                sessionProgram,
                target,
                targetProperties: {
                  currentPromptLevel: decreaseToPromptLevelId,
                  fadingDecrease: 0,
                  fadingIncrease: 0,
                },
              });
            } else {
              this.updateTargetProperties({
                sessionProgram,
                target,
                targetProperties: {
                  fadingDecrease: get(target, 'fadingDecrease', 0) + 1,
                  fadingIncrease: 0,
                },
              });
            }
            return promptLevelFadingData;
          }
        }
      }

      //  If we did not decrease then check if we should increase prompt level
      if ((currentPromptLevelOrder < this.maxTargetPromptOrder(sessionProgram, target))
        && (size(fadingData) >= fadingIncreaseTrials)) {
        const increaseFadingData = chain(fadingData)
          .takeRight(fadingIncreaseTrials || 999999)
          .map((trial) => (
            chain(targetPrompts)
              .find({ id: trial.promptLevel })
              .get('order')
              .value()
          ))
          .value();
        const increasePromptingToOrder = chain(increaseFadingData)
          .uniq()
          .map((promptLevelOrder) => ({
            promptLevelOrder,
            successRate: chain(increaseFadingData)
              .reduce((sum, n) => (sum + ((n <= promptLevelOrder) ? 1 : 0)), 0)
              .divide(fadingIncreaseTrials || size(fadingData))
              .value(),
          }))
          .filter(
            (promptLevel) => Math.round(promptLevel.successRate * 100) >= fadingIncreasePercentage,
          )
          .minBy('promptLevelOrder')
          .get('promptLevelOrder')
          .defaultTo(null)
          .value();

        if ((increasePromptingToOrder !== null)
          && (increasePromptingToOrder > currentPromptLevelOrder)) {
          // Aim for one lower than currently reached prompt level
          if ((get(target, 'fadingIncrease', 0) + 1) >= fadingIncreaseSessions) {
            const increaseToPromptLevelId = chain(targetPrompts)
              .find({ order: increasePromptingToOrder })
              .get('id')
              .value();
            this.updateTargetProperties({
              sessionProgram,
              target,
              targetProperties: {
                currentPromptLevel: increaseToPromptLevelId,
                fadingDecrease: 0,
                fadingIncrease: 0,
              },
            });
          }
          this.updateTargetProperties({
            sessionProgram,
            target,
            targetProperties: {
              fadingDecrease: 0,
              fadingIncrease: get(target, 'fadingIncrease', 0) + 1,
            },
          });
        }
      }
      return promptLevelFadingData;
    }, // analyzePromptLevelFadingMostToLeast()

    getPromptLevelScoreValue(sessionProgram, target, newData, paramTargetPrompts = null) {
      if (newData == null) {
        return {};
      }

      const targetPrompts = paramTargetPrompts || reject(
        this.targetPrompts(sessionProgram, target),
        { excludeFromPromptLevelFading: true },
      );

      const currentPromptLevel = this.currentPromptLevel(sessionProgram, target);
      const currentPromptLevelOrder = get(currentPromptLevel, 'order');

      const newPromptLevelOrder = targetPrompts.find((prompt) => prompt.id === newData.promptLevel)?.order;

      if (newPromptLevelOrder === null || currentPromptLevelOrder === null) {
        return {};
      }

      const promptLevelScoreValue = newPromptLevelOrder <= currentPromptLevelOrder ? 100 : 0;

      return {
        currentPromptLevel: get(currentPromptLevel, 'id', null),
        promptLevelScoreValue,
      };
    }, // getPromptLevelScoreValue()

    async showConnectToCallDialog() {
      if (this.acceptCallsImmediately) {
        this.connectToVideoCall();
        this.notifyInfo(this.$t('run.collectData.notifyVideoCallAutoAccepted', { userName: 'Other party' }));
        return;
      }
      await this.loadVideoSessionMembersNames();
      this.$q
        .dialog({
          title: this.$t('sessions.modals.connectToCall.title', {
            userName: this.videoSessionMembersNames[this.currentVideoCallSession.runBy],
          }),
          message: this.$t('sessions.modals.connectToCall.message'),
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.connectToVideoCall();
          sessionStorage.setItem(IS_INVITATION_ACCEPTED, true);
        });
    }, // showConnectToCallDialog()

    getClientProperty(clientId, property) {
      return chain(this.clients)
        .find({ id: clientId })
        .get(property)
        .value();
    },

    // e2e hook value for a participant tab — the displayed label (alias, else client name), dashed.
    participantTabId(clientId) {
      return (this.getClientProperty(clientId, 'alias')
        || this.getClientProperty(clientId, 'clientName'))?.replace(/ /g, '-');
    },

    getClientPhoto(clientId) {
      const client = find(this.clients, { id: clientId });
      return this.getClientProfileImagePath(client);
    },

    handleFinishedSession() {
      if (!this.hasTherapistCredentials) {
        return this.gotoHomeScreen(this.$t('run.collectData.dialogSessionFinishMessage2'));
      }

      return this.$q.dialog({
        title: this.$t('run.collectData.dialogSessionFinishTitle'),
        message: this.$t('run.collectData.dialogRedirectToEditSessionPage'),
        cancel: true,
        persistent: true,
      }).onOk(() => {
        this.unsavedChanges = false;
        this.preventEndedByOtherMessage = true;
        this.$router.push(`/history/${this.activeSession.id}`);
      }).onCancel(() => {
        this.redirectToHomePage();
      });
    },

    checkOrientation() {
      this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
    },

    updateHeaderHeight() {
      if (this.$refs.header) {
        this.headerHeightPx = this.$refs.header?.$el.offsetHeight;
      }
    }, // updateHeaderHeight()
  },
};
</script>
<style scoped>
.q-tab--active .q-avatar__content {
  box-shadow: 0 0 2px 2px rgba(0,0,0,.35);
}
</style>

<style lang="scss">
#collect-data {
  padding-bottom: 75px;
}

.collect-data-safe-padding-top {
  padding-top: env(safe-area-inset-top); // for iOS 11.2 +
}

.pinned-targets {
  overflow-x: auto;
  white-space: nowrap;
  padding: 3px 0px;

  @media (hover: hover) and (pointer: fine) {
    &::-webkit-scrollbar {
      height: 16px;
    }

    &::-webkit-scrollbar-track:horizontal {
      border-bottom: 8px solid rgba(0, 0, 0, .1);
      margin-left: 8px;
    }

    &::-webkit-scrollbar-thumb:horizontal {
      border-bottom: 8px solid #fff;
    }
  }

  @media only screen and (max-width: $breakpoint-xs-max) {
    margin-top: 0;
  }
}

.file-text-ellipsis .q-chip__content {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding-left: 5px;
}

.hide {
  opacity: 0;
}

.mobile-video-chat-opened {
  padding: 0 !important;
  margin-bottom: 0 !important;

  .collect-data-container {
    height: 100%;
  }
}

.finish-session-dialog {
  .q-radio {
    align-items: flex-start;

    &__label {
      align-self: center;
    }
  }
}

.offline-mode-button {
  margin: 0 10px;
}

</style>
